/**
 * Jarvis — admin AI voice assistant (web), Iron-Man style.
 * "Wake up Jarvis" → power-up boot sequence (HUD + sound) → spoken morning
 * briefing (today's focus, project + onboarding statuses). Then talk (mic →
 * Whisper) or type; Jarvis reasons with Claude + live app tools, replies via TTS.
 */
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Bot, Mic, Send, X, Loader2, Square, Volume2, VolumeX, Sparkles, Mail, ShieldCheck, Lock, Check, Paperclip, Power, Minus, Ruler, CloudSun, Newspaper, ListChecks, Wind, Droplets, Thermometer, ExternalLink, CalendarDays, Copy, Bell, BellOff, Settings as SettingsIcon } from "lucide-react";
import { useJarvisStatus } from "./jarvisStatus";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const uid = () => `jv-${Math.random().toString(36).slice(2)}-${Date.now()}`;

const SPEECH_LANG = { ro: "ro-RO", no: "nb-NO", en: "en-US", pl: "pl-PL" };

const BOOT_LINES = ["INITIALIZING J.A.R.V.I.S.", "CALIBRATING NEURAL CORE", "LINKING APP SYSTEMS", "ALL SYSTEMS ONLINE"];

const IDLE = {
  ro: "Spune-mi ce ai nevoie — proiecte, daily plans, contracte, sau generez ceva.",
  no: "Si hva du trenger — prosjekter, dagsplaner, kontrakter, eller jeg lager noe.",
  en: "Tell me what you need — projects, daily plans, contracts, or I can create something.",
  pl: "Powiedz, czego potrzebujesz — projekty, plany dnia, umowy, albo coś stworzę.",
};
// Cinematic startup lines spoken (in English) when Jarvis is woken / asked if he's there.
const STARTUP_LINES = [
  "JARVIS online. All systems are operational. Good evening, sir. What shall we accomplish today?",
  "Welcome back, sir. JARVIS online. Systems are synchronized and ready. How may I assist you?",
  "Good evening, sir. I've brought all systems online. Everything appears to be functioning perfectly. Shall we begin?",
  "JARVIS at your service, sir. Systems initialized, diagnostics complete, and everything is ready for you. What's our first objective?",
  "Welcome back, sir. While you were away, I've kept everything in order. More or less. How may I assist you?",
  "Systems online. Connections secure. JARVIS ready. Good evening, sir. What are we working on today?",
  "Ah, there you are, sir. JARVIS online. Everything is exactly where you left it… almost. Shall we get started?",
  "Good evening, sir. Systems are awake, diagnostics are clear, and I am at your disposal. What's the plan?",
  "JARVIS online. I've completed the system checks and prepared everything for your arrival. Whenever you're ready, sir.",
  "Welcome back, sir. JARVIS online and standing by. The systems are ready, the workspace is prepared, and fortunately, nothing caught fire while you were away. How may I assist you?",
];

// Short presence replies (spoken in English) for "are you there?" checks — no briefing.
const PRESENCE_REPLIES = [
  "Right here, sir.",
  "Always here, sir. Standing by.",
  "At your service, sir.",
  "Here, sir. Awaiting your command.",
  "Present and fully operational, sir.",
  "I never left, sir. How may I help?",
  "Online and listening, sir.",
];
// Detects presence checks in RO/NO/EN/PL ("ești acolo?", "are you there?", "er du der?", "jesteś tam?").
const PRESENCE_RE = /\b(e[sș]ti acolo|e[sș]ti aici|mai e[sș]ti|acolo e[sș]ti|are you (there|here|online|awake)|you (there|online|awake)|still (there|with me)|er du (der|våken|online|her)|jeste[sś] tam|s[lł]yszysz mnie)\b/i;
const isPresenceCheck = (t) => PRESENCE_RE.test((t || "").toLowerCase());

// Detects a request to stop the background music (RO/NO/EN/PL).
const MUSIC_STOP_RE = /\b(opre[sș]te|opriti|stop|gata cu|nu mai|inchide|închide|skru av|stopp|slå av|wy[lł][aą]cz|zatrzymaj)\b.*(muzic|melodi|c[aâ]ntec|music|song|sang|musikk|piesa|piesă|muzyk|piosenk)/i;
const isMusicStop = (t) => MUSIC_STOP_RE.test((t || "").toLowerCase());

// Detects a request for a guided tour / walkthrough (RO/NO/EN/PL).
const TOUR_RE = /\b(f[aă]-?mi un tur|fa un tur|un tur al|arat[aă]-?mi platforma|plimb[aă]-?m[aă]|cum func[tț]ioneaz[aă] tot|give me a tour|show me around|walk me through|guided tour|take me on a tour|omvisning|vis meg rundt|oprowad[zź]|pokaż mi platform)\b/i;
const isTourRequest = (t) => TOUR_RE.test((t || "").toLowerCase());

// Selectable TTS voices (OpenAI via Emergent key) + optional British ElevenLabs.
const VOICE_OPTIONS = [
  { id: "onyx", label: "Onyx — grav, autoritar" },
  { id: "ash", label: "Ash — cald, calm" },
  { id: "echo", label: "Echo — clar, neutru" },
  { id: "fable", label: "Fable — britanic, expresiv" },
  { id: "sage", label: "Sage — blând" },
  { id: "alloy", label: "Alloy — neutru" },
  { id: "nova", label: "Nova — energic" },
  { id: "shimmer", label: "Shimmer — cald" },
  { id: "coral", label: "Coral — prietenos" },
  { id: "british", label: "British JARVIS (ElevenLabs)" },
];

export default function JarvisWidget({ authHeader, lang = "no" }) {
  const L = SPEECH_LANG[lang] ? lang : "no";
  const navigate = useNavigate();
  const { setActive, setBriefing } = useJarvisStatus();
  const [open, setOpen] = useState(false);
  const [booting, setBooting] = useState(false);
  const [bootStep, setBootStep] = useState(0);
  const [speaking, setSpeaking] = useState(false);
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [pendingFiles, setPendingFiles] = useState([]); // staged photos/PDFs → sent WITH the next message
  const [busy, setBusy] = useState(false);
  const [recording, setRecording] = useState(false);
  const [voiceOn, setVoiceOn] = useState(true);
  // Separate toggle: controls ONLY the "new email / new message" notification sounds.
  // Independent of voiceOn so Jarvis can still speak replies silently of alerts.
  const [notifyOn, setNotifyOn] = useState(() => {
    try { return localStorage.getItem("jv_notify") !== "0"; } catch { return true; }
  });
  const toggleNotify = () => setNotifyOn((v) => {
    const nv = !v;
    try { localStorage.setItem("jv_notify", nv ? "1" : "0"); } catch { /* ignore */ }
    return nv;
  });
  const [settingsOpen, setSettingsOpen] = useState(false);
  // Resizable chat panel — size persisted in localStorage.
  const [panelSize, setPanelSize] = useState(() => {
    try {
      const raw = localStorage.getItem("jv_panel_size");
      if (raw) { const s = JSON.parse(raw); return { w: Math.max(320, s.w || 400), h: Math.max(400, s.h || 640) }; }
    } catch { /* ignore */ }
    return { w: 400, h: 640 };
  });
  const startResize = (e) => {
    e.preventDefault();
    const startX = e.clientX, startY = e.clientY;
    const startW = panelSize.w, startH = panelSize.h;
    const onMove = (ev) => {
      // Panel is anchored LEFT, handle on top-RIGHT: drag RIGHT grows width,
      // drag UP grows height.
      const nw = Math.max(320, Math.min(window.innerWidth - 40, startW + (ev.clientX - startX)));
      const nh = Math.max(400, Math.min(window.innerHeight - 40, startH + (startY - ev.clientY)));
      setPanelSize({ w: nw, h: nh });
    };
    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      try { localStorage.setItem("jv_panel_size", JSON.stringify(panelSizeRef.current)); } catch { /* ignore */ }
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };
  const panelSizeRef = useRef(panelSize);
  useEffect(() => { panelSizeRef.current = panelSize; }, [panelSize]);
  const [mail, setMail] = useState({ configured: false, connected: false, email: "" });
  const [mailModal, setMailModal] = useState(false);
  const [mailForm, setMailForm] = useState({ email: "", password: "" });
  const [mailBusy, setMailBusy] = useState(false);
  const [mailErr, setMailErr] = useState("");
  const [bootData, setBootData] = useState({ stats: null, images: [] });
  const [voice, setVoice] = useState(() => { try { return localStorage.getItem("jv_voice") || "onyx"; } catch { return "onyx"; } });
  const pickVoice = (v) => { setVoice(v); try { localStorage.setItem("jv_voice", v); } catch { /* ignore */ } };
  const [awake, setAwake] = useState(false);
  const [pendingForm, setPendingForm] = useState(null);
  const [formVals, setFormVals] = useState({});
  const [measureRef, setMeasureRef] = useState(null); // {name} → asks for a calibration reference
  const [measureRefInput, setMeasureRefInput] = useState("");
  const [lightbox, setLightbox] = useState(null); // { imgs: [url], idx, captions?, title? }
  const [brief, setBrief] = useState(null); // { weather, news, tasks, title }
  const [mailAlert, setMailAlert] = useState(null); // { from, from_addr, subject, uid } — quiet new-email notification
  const lbTouchX = useRef(null);
  const [tour, setTour] = useState(null); // { steps, index }
  const tourAbortRef = useRef(false);
  const tourScrollRef = useRef(false);

  const openLightbox = (url) => {
    const imgs = msgs.filter((mm) => mm.image).map((mm) => mm.image);
    const idx = Math.max(0, imgs.indexOf(url));
    setLightbox({ imgs: imgs.length ? imgs : [url], idx });
  };
  useEffect(() => {
    if (!lightbox) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setLightbox(null);
      else if (e.key === "ArrowRight") setLightbox((lb) => lb && ({ ...lb, idx: (lb.idx + 1) % lb.imgs.length }));
      else if (e.key === "ArrowLeft") setLightbox((lb) => lb && ({ ...lb, idx: (lb.idx - 1 + lb.imgs.length) % lb.imgs.length }));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox]);
  const dayKey = new Date().toISOString().slice(0, 10);
  const MSGS_KEY = `jv_msgs_${dayKey}`;
  const SESSION_KEY = `jv_session_${dayKey}`;
  const sessionId = useRef(null);
  if (!sessionId.current) {
    let s = localStorage.getItem(SESSION_KEY);
    if (!s) { s = uid(); try { localStorage.setItem(SESSION_KEY, s); } catch {} }
    sessionId.current = s;
  }
  const audioRef = useRef(null);
  const scrollRef = useRef(null);
  const bootIvRef = useRef(null);
  const bootToRef = useRef(null);
  const introRef = useRef(null);
  const finishBootRef = useRef(null);
  const fileInputRef = useRef(null);
  const measureInputRef = useRef(null);
  const measureFileRef = useRef(null);
  const briefingAbortRef = useRef(false);
  const ambienceRef = useRef(null);
  // Continuous "conversation mode" (hands-free): tap once, talk until stopped.
  const [convoMode, setConvoMode] = useState(false);
  const convoModeRef = useRef(false);
  const vadStreamRef = useRef(null);
  const vadCtxRef = useRef(null);
  const speakingRef = useRef(false);
  const busyRef = useRef(false);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [msgs, busy]);
  useEffect(() => { speakingRef.current = speaking; }, [speaking]);
  useEffect(() => { busyRef.current = busy; }, [busy]);
  useEffect(() => () => { convoModeRef.current = false; }, []);

  // ---- Persistent memory: restore today's conversation on mount, save on change ----
  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(MSGS_KEY) || "null");
      if (Array.isArray(saved) && saved.length) { setMsgs(saved); setAwake(true); }
    } catch { /* ignore */ }
  }, []); // eslint-disable-line
  useEffect(() => {
    try {
      if (msgs.length) localStorage.setItem(MSGS_KEY, JSON.stringify(msgs.slice(-80)));
    } catch { /* ignore */ }
  }, [msgs]); // eslint-disable-line

  // On unmount (e.g. logout → mount removes the widget), stop ALL audio/briefing
  // immediately so Jarvis doesn't keep talking on the login/other pages.
  useEffect(() => {
    return () => {
      briefingAbortRef.current = true;
      if (bootIvRef.current) { clearInterval(bootIvRef.current); }
      if (bootToRef.current) { clearTimeout(bootToRef.current); }
      try { introRef.current && introRef.current.pause(); } catch { /* ignore */ }
      try { audioRef.current && audioRef.current.pause(); } catch { /* ignore */ }
      stopAmbience();
    };
  }, []); // eslint-disable-line

  const refreshMail = async () => {
    try {
      const { data } = await axios.get(`${API}/doserne/jarvis/email/status`, authHeader());
      setMail(data);
      if (data.email) setMailForm((f) => ({ ...f, email: f.email || data.email }));
    } catch { /* ignore */ }
  };
  useEffect(() => { if (open) refreshMail(); }, [open]); // eslint-disable-line
  useEffect(() => {
    const onFocus = () => open && refreshMail();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [open]); // eslint-disable-line

  // ---- Quiet background new-email watcher --------------------------------
  // Polls the inbox silently; when a brand-new email arrives Jarvis announces
  // ONLY the sender and asks for approval before reading (privacy: colleagues
  // must not overhear personal mail). Reading the body happens only on "Read".
  const mailAlertRef = useRef(false);
  const readLatestEmail = (fromAddr) => {
    setMailAlert(null);
    setOpen(true); setActive(true); setAwake(true);
    const who = fromAddr ? ` from ${fromAddr}` : "";
    send(`Read my latest email${who} aloud and give me a short summary.`, { hidden: true });
  };
  const dismissMailAlert = () => {
    try { audioRef.current && audioRef.current.pause(); } catch { /* ignore */ }
    setMailAlert(null);
  };
  useEffect(() => {
    let stop = false;
    const poll = async () => {
      // Don't interrupt an active briefing / tour / ongoing speech, and never
      // stack two alerts.
      if (booting || tour || speaking || busy || mailAlertRef.current) return;
      try {
        const { data } = await axios.get(`${API}/doserne/jarvis/email/watch`, authHeader());
        if (stop || !data?.connected || !data?.new) return;
        window.dispatchEvent(new CustomEvent("doserne:new-email"));
        setMailAlert({ from: data.from, from_addr: data.from_addr, subject: data.subject, uid: data.uid });
        if (voiceOn && notifyOn) {
          const sender = (data.from || data.from_addr || "someone").split("<")[0].trim();
          playTts(`Sir, you have a new email from ${sender}. Shall I read it?`);
        }
      } catch { /* ignore polling errors */ }
    };
    const iv = setInterval(poll, 90000);
    const first = setTimeout(poll, 8000); // baseline shortly after mount
    return () => { stop = true; clearInterval(iv); clearTimeout(first); };
  }, [booting, tour, speaking, busy, voiceOn, notifyOn]); // eslint-disable-line
  useEffect(() => { mailAlertRef.current = !!mailAlert; }, [mailAlert]);

  const connectMail = async () => {
    setMailErr("");
    if (!mailForm.email.trim() || !mailForm.password) { setMailErr("Introdu adresa și parola."); return; }
    setMailBusy(true);
    try {
      const { data } = await axios.post(`${API}/doserne/jarvis/email/connect`,
        { email: mailForm.email.trim(), password: mailForm.password }, authHeader());
      setMail((m) => ({ ...m, connected: true, email: data.email }));
      setMailForm((f) => ({ ...f, password: "" }));
    } catch (e) {
      setMailErr(e?.response?.data?.detail || "Conectare eșuată.");
    }
    setMailBusy(false);
  };

  const disconnectMail = async () => {
    try {
      await axios.post(`${API}/doserne/jarvis/email/disconnect`, {}, authHeader());
      setMail((m) => ({ ...m, connected: false, email: "" }));
    } catch { /* ignore */ }
  };

  // ---- Cinematic power-up sound (WebAudio, original — no copyrighted asset) ----
  const playPowerUp = () => {
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      const ctx = new Ctx();
      const now = ctx.currentTime;
      // low "power core" hum
      const hum = ctx.createOscillator(); const hg = ctx.createGain();
      hum.type = "sine"; hum.frequency.setValueAtTime(48, now);
      hum.frequency.linearRampToValueAtTime(120, now + 2.4);
      hg.gain.setValueAtTime(0.0001, now);
      hg.gain.exponentialRampToValueAtTime(0.14, now + 0.6);
      hg.gain.exponentialRampToValueAtTime(0.0001, now + 2.7);
      hum.connect(hg).connect(ctx.destination); hum.start(now); hum.stop(now + 2.8);
      // rising sweep (reactor spin-up)
      const o = ctx.createOscillator(); const g = ctx.createGain();
      o.type = "sawtooth";
      o.frequency.setValueAtTime(70, now);
      o.frequency.exponentialRampToValueAtTime(720, now + 1.3);
      g.gain.setValueAtTime(0.0001, now);
      g.gain.exponentialRampToValueAtTime(0.20, now + 0.4);
      g.gain.exponentialRampToValueAtTime(0.0001, now + 1.6);
      o.connect(g).connect(ctx.destination); o.start(now); o.stop(now + 1.7);
      // shimmer + final chime (systems online)
      const o2 = ctx.createOscillator(); const g2 = ctx.createGain();
      o2.type = "sine"; o2.frequency.setValueAtTime(880, now + 1.0);
      o2.frequency.exponentialRampToValueAtTime(1980, now + 1.6);
      g2.gain.setValueAtTime(0.0001, now + 1.0);
      g2.gain.exponentialRampToValueAtTime(0.12, now + 1.25);
      g2.gain.exponentialRampToValueAtTime(0.0001, now + 1.9);
      o2.connect(g2).connect(ctx.destination); o2.start(now + 1.0); o2.stop(now + 2.0);
      [1568, 2093].forEach((f, i) => {
        const c = ctx.createOscillator(); const cg = ctx.createGain();
        c.type = "triangle"; c.frequency.setValueAtTime(f, now + 2.05 + i * 0.12);
        cg.gain.setValueAtTime(0.0001, now + 2.05 + i * 0.12);
        cg.gain.exponentialRampToValueAtTime(0.18, now + 2.1 + i * 0.12);
        cg.gain.exponentialRampToValueAtTime(0.0001, now + 2.55 + i * 0.12);
        c.connect(cg).connect(ctx.destination); c.start(now + 2.05 + i * 0.12); c.stop(now + 2.6 + i * 0.12);
      });
    } catch { /* ignore */ }
  };

  // ---- Cinematic ambient BED under Jarvis's voice (WebAudio, no copyright, no
  // YouTube). Starts on the wake gesture so the browser allows sound; loops
  // continuously and quietly UNDER the narration; fades out when done.
  // Uses ONLY a user-supplied MP3 at /jarvis_music.mp3 — if none exists, stays
  // silent (no synthesized pad; it sounded like a buzz). ----
  const startAmbience = (loop = false) => {
    if (ambienceRef.current) return;
    try {
      const mp3 = new Audio("/jarvis_music.mp3");
      mp3.loop = loop; // tour → loop so it covers the whole presentation
      mp3.volume = 0.0;
      let ok = true;
      mp3.addEventListener("error", () => { ok = false; if (ambienceRef.current?.el === mp3) ambienceRef.current = null; });
      mp3.addEventListener("ended", () => { if (ambienceRef.current?.el === mp3) ambienceRef.current = null; });
      mp3.play().then(() => {
        if (!ok) return;
        let v = 0; const iv = setInterval(() => { v = Math.min(0.03, v + 0.005); mp3.volume = v; if (v >= 0.03) clearInterval(iv); }, 90);
        ambienceRef.current = { type: "mp3", el: mp3, fadeIv: iv };
      }).catch(() => { /* no MP3 → stay silent (no buzz) */ });
    } catch { /* stay silent */ }
  };

  const stopAmbience = () => {
    const a = ambienceRef.current;
    if (!a) return;
    ambienceRef.current = null;
    if (a.type === "mp3") {
      try { if (a.fadeIv) clearInterval(a.fadeIv); } catch { /* ignore */ }
      let v = a.el.volume;
      const iv = setInterval(() => { v = Math.max(0, v - 0.03); a.el.volume = v; if (v <= 0) { clearInterval(iv); try { a.el.pause(); } catch { /* ignore */ } } }, 80);
    }
  };

  const playTts = async (text) => {
    if (!voiceOn || !text) { return; }
    try {
      const res = await axios.post(`${API}/doserne/jarvis/tts`, { text, voice, speed: 1.2 },
        { ...authHeader(), responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      if (audioRef.current) audioRef.current.pause();
      const a = new Audio(url);
      audioRef.current = a;
      setSpeaking(true);
      // Background music (if playing from boot) fades out once the narration ends.
      a.onended = () => { URL.revokeObjectURL(url); setSpeaking(false); };
      a.onpause = () => setSpeaking(false);
      a.play().catch(() => { setSpeaking(false); });
    } catch { setSpeaking(false); }
  };

  // Fetch TTS audio as a blob URL (so we can PREFETCH the next line while the
  // current one is still playing → no gaps between briefing sentences).
  const fetchTts = async (text, spd = 1.15) => {
    if (!voiceOn || !text) return null;
    try {
      const res = await axios.post(`${API}/doserne/jarvis/tts`, { text, voice, speed: spd }, { ...authHeader(), responseType: "blob" });
      return URL.createObjectURL(res.data);
    } catch { return null; }
  };
  const playUrl = (url) => new Promise((resolve) => {
    if (!url) return resolve();
    if (audioRef.current) { try { audioRef.current.pause(); } catch {} }
    const a = new Audio(url);
    audioRef.current = a;
    setSpeaking(true);
    let done = false;
    const fin = () => { if (done) return; done = true; try { URL.revokeObjectURL(url); } catch {} setSpeaking(false); resolve(); };
    a.onended = fin;
    a.onerror = fin;
    a.onpause = fin;
    a.play().catch(fin);
  });

  // Speak a reply with low time-to-first-audio: split into sentence chunks, play
  // the first as soon as it's ready and prefetch the rest while it plays.
  const speakChunked = async (text, spd = 1.2) => {
    if (!voiceOn || !text) return;
    // Flag "speaking" synchronously so hands-free conversation mode pauses the mic
    // immediately (before the first TTS blob has even come back).
    speakingRef.current = true;
    setSpeaking(true);
    const parts = (text.match(/[^.!?…]+[.!?…]*/g) || [text]).map((s) => s.trim()).filter(Boolean);
    const chunks = [];
    let cur = "";
    for (const p of parts) {
      cur = cur ? `${cur} ${p}` : p;
      if (cur.length >= 60) { chunks.push(cur); cur = ""; }
    }
    if (cur) chunks.push(cur);
    if (!chunks.length) { speakingRef.current = false; setSpeaking(false); return; }
    let nextUrl = fetchTts(chunks[0], spd);
    for (let i = 0; i < chunks.length; i++) {
      const url = await nextUrl;
      nextUrl = i + 1 < chunks.length ? fetchTts(chunks[i + 1], spd) : Promise.resolve(null);
      await playUrl(url);
    }
    speakingRef.current = false;
  };

  // ---- Interrupt: stop boot / intro / speech immediately ----
  // ---- Guided tour: self-driving walkthrough narrated by Jarvis ----
  const stopTour = () => {
    tourAbortRef.current = true;
    tourScrollRef.current = false;
    setTour(null);
    setActive(false);
    // Music keeps playing until the track ends (say "stop the music" to cut it).
    if (audioRef.current) { try { audioRef.current.pause(); } catch {} }
    setSpeaking(false);
  };
  // Gently scroll the current page up & down while Jarvis narrates, so the tour
  // feels alive and actually shows the content instead of sitting at the top.
  const autoScrollDuring = async () => {
    const wait = (ms) => new Promise((r) => setTimeout(r, ms));
    try { window.scrollTo({ top: 0, behavior: "smooth" }); } catch {}
    await wait(700);
    let goingDown = true;
    while (tourScrollRef.current && !tourAbortRef.current) {
      const max = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
      if (max <= 60) { await wait(700); continue; }
      const cur = window.scrollY || window.pageYOffset || 0;
      let next;
      if (goingDown) {
        next = Math.min(cur + window.innerHeight * 0.55, max);
        if (next >= max - 5) goingDown = false;
      } else {
        next = Math.max(cur - window.innerHeight * 0.55, 0);
        if (next <= 5) goingDown = true;
      }
      try { window.scrollTo({ top: next, behavior: "smooth" }); } catch {}
      await wait(1700);
    }
  };
  // Perform a per-step visual action while Jarvis narrates (tabs / signature).
  const runTourAction = async (action) => {
    const wait = (ms) => new Promise((r) => setTimeout(r, ms));
    if (action === "project_tabs") {
      const clickTab = (key) => { try { document.querySelector(`[data-testid="tab-${key}"]`)?.click(); } catch {} };
      await wait(1600);
      for (const key of ["info", "underlag", "kontroll", "sja", "progress"]) {
        if (tourAbortRef.current) return;
        clickTab(key);
        await wait(1900);
      }
    } else if (action === "draw_signature") {
      await wait(1800);
      if (tourAbortRef.current) return;
      const cv = document.querySelector('[data-testid="sign-canvas"]');
      if (!cv) return;
      try {
        const ctx = cv.getContext("2d");
        const w = cv.width, h = cv.height;
        ctx.save();
        ctx.setTransform(1, 0, 0, 1, 0, 0); // draw in raw buffer coords, always visible
        ctx.strokeStyle = "#0f172a";
        ctx.lineWidth = Math.max(2, h * 0.02);
        ctx.lineCap = "round";
        ctx.lineJoin = "round";
        const pts = [];
        const n = 36;
        for (let i = 0; i <= n; i++) {
          const t = i / n;
          const x = w * 0.08 + t * w * 0.84;
          const y = h * 0.55 + Math.sin(t * Math.PI * 3.2) * h * 0.22 - (t > 0.5 ? (t - 0.5) * h * 0.25 : 0);
          pts.push([x, y]);
        }
        ctx.beginPath();
        ctx.moveTo(pts[0][0], pts[0][1]);
        for (let i = 1; i < pts.length; i++) {
          if (tourAbortRef.current) break;
          ctx.lineTo(pts[i][0], pts[i][1]);
          ctx.stroke();
          await wait(45);
        }
        ctx.restore();
      } catch { /* visual only */ }
    }
  };
  const runTour = async (steps) => {
    if (!steps?.length) return;
    tourAbortRef.current = false;
    setOpen(false); // hide the chat panel — the voice HUD leads the presentation
    setTour({ steps, index: 0, caption: "" });
    // Presentation soundtrack: same quiet volume, looped so it covers the tour.
    try { startAmbience(true); } catch { /* ignore */ }
    const wait = (ms) => new Promise((r) => setTimeout(r, ms));
    for (let i = 0; i < steps.length; i++) {
      if (tourAbortRef.current) break;
      const step = steps[i];
      // No chat panel while presenting — the caption rides the sci-fi voice bar.
      setTour({ steps, index: i, caption: step.say });
      // Final step is Jarvis himself → the cinematic HUD is his "page".
      setActive(step.page === "jarvis" ? true : !!step.hud);
      if (step.route) {
        try { navigate(step.route); } catch { /* stay */ }
        await wait(1100); // let the page mount before narrating
      }
      if (tourAbortRef.current) break;
      // Kick off the gentle scroll + any visual action (both run alongside the
      // narration), then narrate at a calmer, more human pace.
      tourScrollRef.current = true;
      autoScrollDuring();
      if (step.action) runTourAction(step.action);
      await speakChunked(step.say, 1.0);
      tourScrollRef.current = false;
      try { window.scrollTo({ top: 0, behavior: "smooth" }); } catch {}
      if (tourAbortRef.current) break;
      await wait(650);
    }
    tourScrollRef.current = false;
    // Let the music finish its current pass, then stop on its own.
    try { if (ambienceRef.current?.el) ambienceRef.current.el.loop = false; } catch { /* ignore */ }
    // We END on the Jarvis command center (HUD) — leave it up. Only a manual
    // Stop tears things down.
    setTour(null);
  };

  const stopJarvis = () => {
    briefingAbortRef.current = true;
    setBriefing(null);
    if (bootIvRef.current) { clearInterval(bootIvRef.current); bootIvRef.current = null; }
    if (bootToRef.current) { clearTimeout(bootToRef.current); bootToRef.current = null; }
    if (introRef.current) { try { introRef.current.pause(); } catch {} introRef.current = null; }
    if (audioRef.current) { try { audioRef.current.pause(); } catch {} }
    stopAmbience();
    setBooting(false);
    setSpeaking(false);
  };

  const greetText = () => STARTUP_LINES[Math.floor(Math.random() * STARTUP_LINES.length)];

  // ---- Cinematic, section-by-section morning briefing (no long LLM wait) ----
  const COND = {
    ro: { clear: "cer senin", cloud: "înnorat", rain: "ploaie", snow: "ninsoare", fog: "ceață", default: "vreme mixtă" },
    no: { clear: "klart", cloud: "skyet", rain: "regn", snow: "snø", fog: "tåke", default: "vekslende vær" },
    en: { clear: "clear skies", cloud: "cloudy", rain: "rain", snow: "snow", fog: "fog", default: "mixed weather" },
    pl: { clear: "bezchmurnie", cloud: "pochmurno", rain: "deszcz", snow: "śnieg", fog: "mgła", default: "zmienna pogoda" },
  };
  const condKey = (c) => {
    c = (c || "").toLowerCase();
    if (/clear|sun|fair/.test(c)) return "clear";
    if (/rain|drizzle|shower/.test(c)) return "rain";
    if (/snow|sleet/.test(c)) return "snow";
    if (/fog|mist/.test(c)) return "fog";
    if (/cloud|overcast|partly/.test(c)) return "cloud";
    return "default";
  };
  const wxLine = (w = {}) => {
    const c = COND[L][condKey(w.condition)];
    const t = w.temperature_c != null ? Math.round(w.temperature_c) : null;
    const wd = w.wind_ms;
    return {
      ro: `În Oslo${t != null ? ` sunt ${t} grade` : ""}, ${c}${wd != null ? `, vânt de ${wd} metri pe secundă` : ""}.`,
      no: `I Oslo${t != null ? ` er det ${t} grader` : ""}, ${c}${wd != null ? `, vind ${wd} meter i sekundet` : ""}.`,
      en: `In Oslo${t != null ? ` it's ${t} degrees` : ""}, ${c}${wd != null ? `, wind ${wd} meters per second` : ""}.`,
      pl: `W Oslo${t != null ? ` jest ${t} stopni` : ""}, ${c}${wd != null ? `, wiatr ${wd} metrów na sekundę` : ""}.`,
    }[L];
  };
  const projLine = (p) => {
    const auth = (p.authors || []).join(", ");
    const n = p.photo_count || 0;
    return {
      ro: `${p.name}. Ieri ${auth || "echipa"} ${n ? `a adăugat ${n} ${n === 1 ? "poză" : "poze"}` : "a lucrat aici"}.`,
      no: `${p.name}. I går ${auth || "teamet"} ${n ? `la til ${n} ${n === 1 ? "bilde" : "bilder"}` : "jobbet her"}.`,
      en: `${p.name}. Yesterday ${auth || "the team"} ${n ? `posted ${n} ${n === 1 ? "photo" : "photos"}` : "worked here"}.`,
      pl: `${p.name}. Wczoraj ${auth || "zespół"} ${n ? `dodał ${n} zdjęć` : "pracował tutaj"}.`,
    }[L];
  };
  const progressIntro = (count) => ({
    ro: count ? `Progresul de ieri: ${count} ${count === 1 ? "proiect actualizat" : "proiecte actualizate"}.` : "Ieri nu au fost actualizări pe proiecte.",
    no: count ? `Gårsdagens fremdrift: ${count} ${count === 1 ? "prosjekt oppdatert" : "prosjekter oppdatert"}.` : "Ingen prosjektoppdateringer i går.",
    en: count ? `Yesterday's progress: ${count} ${count === 1 ? "project updated" : "projects updated"}.` : "No project updates yesterday.",
    pl: count ? `Wczorajszy postęp: zaktualizowano ${count} projektów.` : "Wczoraj brak aktualizacji projektów.",
  }[L]);
  const mailIntro = (unread, n) => ({
    ro: unread ? `Ai ${unread} emailuri necitite. Iată cele mai recente:` : "Iată cele mai recente emailuri:",
    no: unread ? `Du har ${unread} uleste e-poster. Her er de nyeste:` : "Her er de nyeste e-postene:",
    en: unread ? `You have ${unread} unread emails. Here are the latest:` : "Here are the latest emails:",
    pl: unread ? `Masz ${unread} nieprzeczytanych e-maili. Oto najnowsze:` : "Oto najnowsze e-maile:",
  }[L]);
  const mailItemLine = (m) => {
    const subj = (m.subject || "").replace(/\s+/g, " ").trim().slice(0, 120);
    const src = (m.summary || m.preview || "").replace(/\s+/g, " ").trim();
    const pv = m.summary ? src.slice(0, 220) : src.slice(0, 180);
    const intro = { ro: "Pe scurt", no: "Kort sagt", en: "In short", pl: "W skrócie" }[L];
    const tail = pv ? ` ${intro}: ${pv}` : "";
    return {
      ro: `De la ${m.from}. Subiect: ${subj}.${tail}`,
      no: `Fra ${m.from}. Emne: ${subj}.${tail}`,
      en: `From ${m.from}. Subject: ${subj}.${tail}`,
      pl: `Od ${m.from}. Temat: ${subj}.${tail}`,
    }[L];
  };

  const runBriefing = async (dataPromise) => {
    briefingAbortRef.current = false;
    let hud = {}, ytd = { projects: [] };
    try {
      const [h, y] = dataPromise ? await dataPromise : await Promise.all([
        axios.get(`${API}/doserne/jarvis/hud`, authHeader()),
        axios.get(`${API}/doserne/jarvis/yesterday-progress`, authHeader()),
      ]);
      hud = h.data || {}; ytd = y.data || { projects: [] };
    } catch { /* offline → skip */ }

    const steps = [];
    // 0) First-ever open → Jarvis introduces himself, then continues.
    try {
      if (!localStorage.getItem("jv_introduced")) {
        steps.push({ text: "Allow me to introduce myself, sir. I am JARVIS — your dedicated assistant for the Doserne platform. I can brief you each day on your projects and daily plans, read and summarize your emails, keep your to-do list, generate contracts and documents, and take you anywhere in the platform on command. Now, allow me to bring you up to speed." });
        localStorage.setItem("jv_introduced", "1");
      }
    } catch { /* ignore */ }
    // 1) Greeting + weather + progress intro as ONE fluid opening chunk.
    const yprojects = (ytd.projects || []).slice(0, 6);
    const opening = [greetText(), wxLine(hud.weather), progressIntro(yprojects.length)].filter(Boolean).join(" ");
    steps.push({ text: opening });
    // 2) One chunk per updated project (drives the zoom spotlight + photos).
    yprojects.forEach((p) => steps.push({
      text: projLine(p),
      focus: { phase: "project", focusId: p.project_id, focusName: p.name, color: p.color, photos: p.photos || [], authors: p.authors || [] },
    }));
    // 3) Emails — narrate the latest ones one by one, spotlighting each.
    const emails = (hud.emails?.recent || []).slice(0, 4);
    if ((hud.emails?.unread != null) || emails.length) {
      steps.push({ text: mailIntro(hud.emails?.unread, emails.length) });
    }
    emails.forEach((m) => steps.push({
      text: mailItemLine(m),
      focus: { phase: "email", email: { uid: m.uid, from: m.from, subject: m.subject, date: m.date, unread: m.unread, preview: m.preview, summary: m.summary } },
    }));

    // Pipeline: prefetch the next line's audio while the current one plays.
    let nextUrl = fetchTts(steps[0]?.text);
    for (let i = 0; i < steps.length; i++) {
      if (briefingAbortRef.current) break;
      setBriefing(steps[i].focus || null);
      setMsgs((m) => [...m, { role: "assistant", text: steps[i].text }]);
      const url = await nextUrl;
      nextUrl = i + 1 < steps.length ? fetchTts(steps[i + 1].text) : Promise.resolve(null);
      await playUrl(url);
    }
    setBriefing(null);
    if (!briefingAbortRef.current) setMsgs((m) => [...m, { role: "assistant", text: IDLE[L] }]);
    // Note: do NOT stop the music here — let the track keep playing to the end.
    // The admin can stop it anytime ("oprește muzica") or via Stop/Shutdown.
  };

  // ---- Send one or more photos/PDFs straight to Jarvis to analyze ("see" them) ----
  // ---- Stage one or more photos/PDFs in the composer, to be sent WITH the
  // next typed message (like attaching a file here in chat) — NOT analyzed
  // immediately. The paperclip and clipboard PASTE both stage. ----
  const stageFiles = (all) => {
    const arr = (all || []).filter(Boolean);
    if (!arr.length) return;
    const ok = arr.filter((f) => { const ct = (f.type || "").toLowerCase(); return ct.startsWith("image/") || ct === "application/pdf"; });
    if (ok.length < arr.length) {
      setOpen(true); setMsgs((m) => [...m, { role: "assistant", text: "Pot atașa doar imagini sau PDF.", error: true }]);
    }
    if (!ok.length) return;
    setOpen(true);
    setPendingFiles((prev) => [...prev, ...ok].slice(0, 8));
  };
  const removePending = (idx) => setPendingFiles((prev) => prev.filter((_, i) => i !== idx));

  const analyzeFile = async (e) => {
    const all = Array.from(e.target.files || []);
    e.target.value = "";
    stageFiles(all);
  };

  // Core: send one or more image/PDF files to Jarvis to "see". Reused by the
  // attach button and by clipboard PASTE (Ctrl+V).
  const analyzeFiles = async (all) => {
    if (!all || !all.length) return;
    const files = all.slice(0, 8);
    const bad = files.find((f) => { const ct = (f.type || "").toLowerCase(); return !(ct.startsWith("image/") || ct === "application/pdf"); });
    if (bad) {
      setOpen(true); setMsgs((m) => [...m, { role: "assistant", text: "Pot analiza doar imagini sau PDF.", error: true }]); return;
    }
    const totalSize = files.reduce((s, f) => s + f.size, 0);
    if (totalSize > 40 * 1024 * 1024) {
      setOpen(true); setMsgs((m) => [...m, { role: "assistant", text: "Fișierele sunt prea mari (max 40MB în total).", error: true }]); return;
    }
    setOpen(true);
    const label = files.length === 1 ? `📎 ${files[0].name}` : `📎 ${files.length} fișiere: ${files.map((f) => f.name).join(", ")}`;
    const instruction = input.trim();
    setMsgs((m) => [...m, { role: "user", text: instruction ? `${label}\n${instruction}` : label }]);
    setBusy(true);
    // With a text instruction → route the file(s) THROUGH Jarvis's tool pipeline
    // (transcribe → chat), so he can act (e.g. create Underlag drafts from a
    // Tripletex hours photo) instead of only summarizing. Files alone → summary.
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      fd.append("lang", L);
      if (instruction) {
        fd.append("message", instruction);
        fd.append("session_id", sessionId.current);
        const { data } = await axios.post(`${API}/doserne/jarvis/chat-with-files`, fd, authHeader());
        setInput("");
        const tools = (data.actions || []).map((a) => ({ name: a.tool, status: a.result_status || "ok" }));
        setMsgs((m) => [...m, { role: "assistant", text: data.reply || "…", tools, streaming: false }]);
        if (data.reply) speakChunked(data.reply);
        applyChatSideEffects(data);
      } else {
        const { data } = await axios.post(`${API}/doserne/jarvis/analyze-file`, fd, authHeader());
        setMsgs((m) => [...m, { role: "assistant", text: data.content || "…" }]);
        playTts(data.content || "");
      }
    } catch (err) {
      setMsgs((m) => [...m, { role: "assistant", text: "⚠️ " + (err?.response?.data?.detail || "Nu am putut analiza fișierele."), error: true }]);
    }
    setBusy(false);
  };

  // Measure facade m² from an architectural drawing (PDF/image) for a quote.
  const measureFacades = async (files, reference = "") => {
    const arr = (Array.isArray(files) ? files : [files]).filter(Boolean).slice(0, 6);
    if (!arr.length) return;
    const bad = arr.find((f) => {
      const ct = (f.type || "").toLowerCase();
      const isIfc = (f.name || "").toLowerCase().endsWith(".ifc");
      return !(isIfc || ct.startsWith("image/") || ct === "application/pdf");
    });
    if (bad) {
      setOpen(true); setMsgs((m) => [...m, { role: "assistant", text: "Pot măsura doar din desene PDF/imagine sau un model IFC.", error: true }]); return;
    }
    const totalDraw = arr.filter((f) => !(f.name || "").toLowerCase().endsWith(".ifc")).reduce((s, f) => s + f.size, 0);
    if (totalDraw > 60 * 1024 * 1024) {
      setOpen(true); setMsgs((m) => [...m, { role: "assistant", text: "Fișierele sunt prea mari (max 60MB în total).", error: true }]); return;
    }
    measureFileRef.current = arr;
    setOpen(true);
    const nameList = arr.map((f) => f.name).join(", ");
    const label = reference
      ? `📐 Recalculează cu reper: ${reference}`
      : (arr.length === 1 ? `📐 Măsoară fațadele: ${nameList}` : `📐 Măsoară fațadele din ${arr.length} desene: ${nameList}`);
    setMsgs((m) => [...m, { role: "user", text: label }]);
    setBusy(true); setMeasureRef(null);
    try {
      const fd = new FormData();
      arr.forEach((f) => fd.append("files", f));
      fd.append("lang", L);
      if (reference) fd.append("reference", reference);
      const start = await axios.post(`${API}/doserne/jarvis/measure-facades`, fd, authHeader());
      const jobId = start.data.job_id;
      // Poll until done (vision + calibration can take up to ~2 min).
      let data = null;
      for (let i = 0; i < 60; i++) {
        await new Promise((r) => setTimeout(r, 3000));
        const s = await axios.get(`${API}/doserne/jarvis/measure-facades/${jobId}`, authHeader());
        if (s.data.status === "done") { data = s.data; break; }
        if (s.data.status === "error") throw new Error(s.data.error || "Eroare la măsurare.");
      }
      if (!data) throw new Error("Măsurarea a durat prea mult. Încearcă din nou.");
      const head = { ro: "Măsurători fațade", no: "Fasademål", en: "Facade measurements", pl: "Pomiary elewacji" }[L];
      const srcNote = data.files_count > 1 ? ` (${data.files_count} desene combinate)` : "";
      setMsgs((m) => [...m, { role: "assistant", text: `${head} — ${data.filename}${srcNote}${data.scale ? ` (${data.scale})` : ""}:\n\n${data.table}`, image: data.annotated_image || null }]);
      const spoken = data.summary || `${head}: ${data.total_m2} m².`;
      playTts(spoken);
      if (data.needs_reference) {
        const ask = { ro: "Desenele nu au cote scrise, deci acestea sunt estimări. Dă-mi un reper real ca să recalibrez (ex: 'ușa = 2,1 m' sau 'lățime sud = 9 m').", no: "Tegningene mangler mål, så dette er anslag. Gi meg ett kjent mål for å kalibrere.", en: "The drawings have no written dimensions, so these are estimates. Give me one real reference to recalibrate.", pl: "Rysunki nie mają wymiarów, to szacunki. Podaj jeden znany wymiar do kalibracji." }[L];
        setMsgs((m) => [...m, { role: "assistant", text: ask }]);
        setMeasureRef({ name: nameList });
      }
    } catch (err) {
      setMsgs((m) => [...m, { role: "assistant", text: "⚠️ " + (err?.response?.data?.detail || "Nu am putut măsura fațadele."), error: true }]);
    }
    setBusy(false);
  };
  const respondPresence = (echo) => {
    setOpen(true); setActive(true); setAwake(true);
    if (echo) setMsgs((m) => [...m, { role: "user", text: echo }]);
    const line = PRESENCE_REPLIES[Math.floor(Math.random() * PRESENCE_REPLIES.length)];
    setMsgs((m) => [...m, { role: "assistant", text: line }]);
    playTts(line);
  };

  const send = async (text, opts = {}) => {
    const message = (text ?? input).trim();
    if (!message || busy) return;
    // Stop the background music on command → no LLM round-trip.
    if (isMusicStop(message)) {
      if (!opts.hidden) { setInput(""); setMsgs((m) => [...m, { role: "user", text: message }]); }
      stopAmbience();
      const line = { ro: "Am oprit muzica, domnule.", no: "Musikken er stoppet, sir.", en: "Music stopped, sir.", pl: "Muzyka zatrzymana." }[L];
      setMsgs((m) => [...m, { role: "assistant", text: line }]);
      playTts(line);
      return;
    }
    // Presence check → short spoken acknowledgement, skip the LLM + briefing.
    if (isPresenceCheck(message)) {
      if (!opts.hidden) { setInput(""); setMsgs((m) => [...m, { role: "user", text: message }]); }
      const line = PRESENCE_REPLIES[Math.floor(Math.random() * PRESENCE_REPLIES.length)];
      setMsgs((m) => [...m, { role: "assistant", text: line }]);
      playTts(line);
      return;
    }
    // Guided tour → run the scripted walkthrough directly (no LLM round-trip).
    if (isTourRequest(message)) {
      if (!opts.hidden) { setInput(""); setMsgs((m) => [...m, { role: "user", text: message }]); }
      try {
        const { data } = await axios.get(`${API}/doserne/jarvis/tour?lang=en`, authHeader());
        if (data?.steps?.length) { runTour(data.steps); return; }
      } catch { /* fall through to the LLM */ }
    }
    if (!opts.hidden) setInput("");
    if (!opts.hidden) setMsgs((m) => [...m, { role: "user", text: message }]);
    setBusy(true);

    // ELITE streaming: push an empty assistant bubble and progressively fill it.
    setMsgs((m) => [...m, { role: "assistant", text: "", tools: [], streaming: true }]);
    const updateLast = (patch) => setMsgs((m) => {
      const copy = [...m];
      const idx = copy.length - 1;
      copy[idx] = typeof patch === "function" ? patch(copy[idx]) : { ...copy[idx], ...patch };
      return copy;
    });

    let finalData = null;
    let finalReply = "";
    try {
      const token = localStorage.getItem("token") || "";
      const res = await fetch(`${API}/doserne/jarvis/chat/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
        body: JSON.stringify({ session_id: sessionId.current, message }),
      });
      if (!res.ok || !res.body) throw new Error(`stream_${res.status}`);
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith("data:")) continue;
          try {
            const ev = JSON.parse(line.slice(5).trim());
            if (ev.type === "delta") {
              finalReply += ev.text || "";
              updateLast((cur) => ({ ...cur, text: (cur.text || "") + (ev.text || "") }));
            } else if (ev.type === "tool_start") {
              updateLast((cur) => ({ ...cur, tools: [...(cur.tools || []), { name: ev.name, status: "running" }] }));
            } else if (ev.type === "tool_done") {
              updateLast((cur) => ({
                ...cur,
                tools: (cur.tools || []).map((t, i, arr) => (
                  i === arr.length - 1 || (t.name === ev.name && t.status === "running")
                    ? { ...t, name: ev.name, status: ev.status || "ok" } : t
                )),
              }));
            } else if (ev.type === "done") {
              finalData = ev;
            } else if (ev.type === "error") {
              updateLast((cur) => ({ ...cur, error: true, text: (cur.text || "") + "\n⚠️ " + (ev.message || "Error") }));
            }
          } catch { /* skip malformed sse */ }
        }
      }
      updateLast({ streaming: false });
      if (finalReply) speakChunked(finalReply);
      if (finalData) applyChatSideEffects(finalData);
    } catch (e) {
      // Fallback to non-streaming endpoint if the stream failed.
      try {
        const { data } = await axios.post(`${API}/doserne/jarvis/chat`,
          { session_id: sessionId.current, message }, authHeader());
        updateLast({ streaming: false, text: data.reply || "…", tools: (data.actions || []).map((a) => ({ name: a.tool, status: a.result_status || "ok" })) });
        if (data.reply) speakChunked(data.reply);
        applyChatSideEffects(data);
      } catch (e2) {
        updateLast({ streaming: false, error: true, text: "⚠️ " + (e2?.response?.data?.detail || e?.message || "Jarvis error") });
      }
    }
    setBusy(false);
  };

  // Non-reply side effects shared by streaming + non-streaming paths.
  const applyChatSideEffects = (data) => {
    const tools = (data.actions || []).map((a) => a.tool);
    if (tools.includes("add_project_line")) {
      window.dispatchEvent(new CustomEvent("doserne:project-jobs-changed"));
    }
    if (data.navigate) setTimeout(() => { try { navigate(data.navigate); } catch { window.location.assign(data.navigate); } }, 600);
    if (data.client_action?.type === "show_hud") setActive(true);
    if (data.client_action?.type === "hide_hud") setActive(false);
    if (data.client_action?.type === "tour" && data.client_action.steps?.length) runTour(data.client_action.steps);
    if (data.client_action?.type === "gallery" && data.client_action.images?.length) {
      const imgs = data.client_action.images.map((x) => x.url);
      const captions = data.client_action.images.map((x) => x.caption || "");
      setLightbox({ imgs, idx: 0, captions, title: data.client_action.title || "" });
    }
    if (data.client_action?.type === "brief_cards" && data.client_action.brief) {
      setBrief({ ...data.client_action.brief, title: data.client_action.title || "" });
    }
    if (data.client_action?.type === "form" && data.client_action.form?.fields?.length) {
      setFormVals({}); setPendingForm(data.client_action.form);
    }
    if (data.client_action?.type === "open_email" && data.client_action.email?.uid) {
      const em = data.client_action.email;
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent("doserne:open-email", { detail: em }));
      }, 1100);
    }
  };

  // Submit the in-chat form Jarvis requested → send all answers as ONE message.
  const submitForm = () => {
    const f = pendingForm;
    if (!f) return;
    const miss = (f.fields || []).find((fl) => fl.required && !(formVals[fl.name] || "").trim());
    if (miss) { setMsgs((m) => [...m, { role: "assistant", text: `Completează „${miss.label}”.`, error: true }]); return; }
    const lines = (f.fields || []).map((fl) => `${fl.label}: ${(formVals[fl.name] || "").trim() || "-"}`);
    const text = `${f.title}:\n${lines.join("\n")}`;
    setPendingForm(null);
    setFormVals({});
    send(text);
  };

  // ---- Wake sequence ----
  const wake = async () => {
    if (booting) return;
    setOpen(true);
    setActive(true);
    setAwake(true);
    setMsgs([]);
    setBooting(true);
    setBootStep(0);
    briefingAbortRef.current = false;
    // Kick off the briefing data fetch NOW (during the ~2.9s boot animation) so
    // it's ready the moment the boot finishes — no wait before Jarvis speaks.
    const dataP = Promise.all([
      axios.get(`${API}/doserne/jarvis/hud`, authHeader()),
      axios.get(`${API}/doserne/jarvis/yesterday-progress`, authHeader()),
    ]).catch(() => null);
    axios.get(`${API}/doserne/jarvis/boot-visuals`, authHeader())
      .then(({ data }) => setBootData(data)).catch(() => {});
    let step = 0;
    bootIvRef.current = setInterval(() => { step += 1; setBootStep(step); }, 620);
    const finishBoot = () => {
      if (bootIvRef.current) { clearInterval(bootIvRef.current); bootIvRef.current = null; }
      if (introRef.current) { try { introRef.current.pause(); } catch {} introRef.current = null; }
      setBooting(false);
      // Greeting + weather + progress + per-project + emails, all pipelined for
      // fluid, gap-free narration (data already prefetched above).
      setMsgs([]);
      runBriefing(dataP);
    };
    finishBootRef.current = finishBoot;
    playPowerUp();
    startAmbience();
    bootToRef.current = setTimeout(finishBoot, 2900);
  };

  // Wake-word listening REMOVED — Jarvis never uses the mic passively (privacy for
  // colleagues). He wakes only on click. Speech is hands-free CONVERSATION mode
  // (toggle the mic button) with silence detection — see startConvo below.

  const stopRec = () => {
    setRecording(false);
  };

  // ---- Paste an image straight into the chat (Ctrl+V) → Jarvis analyzes it ----
  const handlePaste = (e) => {
    const items = Array.from((e.clipboardData && e.clipboardData.items) || []);
    const files = items
      .filter((it) => it.kind === "file" && (it.type || "").startsWith("image/"))
      .map((it) => it.getAsFile())
      .filter(Boolean);
    if (files.length) {
      e.preventDefault();
      stageFiles(files);
    }
  };

  // Send the composer: if photo(s)/PDF(s) are staged, send them TOGETHER with the
  // typed message through Jarvis's tool pipeline; otherwise a plain text message.
  const submitComposer = () => {
    if (busy) return;
    if (pendingFiles.length) {
      const files = pendingFiles;
      setPendingFiles([]);
      analyzeFiles(files);
    } else {
      send();
    }
  };

  // Copy a message's text to the clipboard (Ctrl+C works too; this is a shortcut).
  const [copiedIdx, setCopiedIdx] = useState(null);
  const copyMsg = async (text, i) => {
    try {
      await navigator.clipboard.writeText(text || "");
      setCopiedIdx(i);
      setTimeout(() => setCopiedIdx((c) => (c === i ? null : c)), 1400);
    } catch { /* ignore */ }
  };

  // ---- Hands-free CONVERSATION mode: tap once → talk continuously until stopped.
  // Records one utterance at a time with voice-activity (silence) detection,
  // transcribes it, sends it, and — while Jarvis speaks — pauses the mic so it
  // never captures its own voice. Resumes automatically after each reply.
  const recordUtterance = (stream, analyser, buf) => new Promise((resolve) => {
    let mr;
    try {
      const mime = MediaRecorder.isTypeSupported("audio/webm;codecs=opus") ? "audio/webm;codecs=opus" : "audio/webm";
      mr = new MediaRecorder(stream, { mimeType: mime });
    } catch { return resolve(null); }
    const chunks = [];
    mr.ondataavailable = (ev) => { if (ev.data.size) chunks.push(ev.data); };
    mr.onstop = () => resolve(chunks.length ? new Blob(chunks, { type: "audio/webm" }) : null);
    mr.start();
    setRecording(true);
    const THRESH = 0.014;   // RMS speech threshold
    const SILENCE_MS = 1200; // trailing silence that ends an utterance
    const MAX_MS = 15000;    // hard cap per utterance
    const NO_SPEECH_MS = 9000; // give up if nobody speaks
    const t0 = performance.now();
    let lastLoud = t0;
    let heard = false;
    const tick = () => {
      if (!convoModeRef.current) { try { if (mr.state !== "inactive") mr.stop(); } catch {} return; }
      analyser.getFloatTimeDomainData(buf);
      let sum = 0;
      for (let i = 0; i < buf.length; i++) sum += buf[i] * buf[i];
      const rms = Math.sqrt(sum / buf.length);
      const now = performance.now();
      if (rms > THRESH) { heard = true; lastLoud = now; }
      const speechEnded = heard && now - lastLoud > SILENCE_MS;
      const tooLong = now - t0 > MAX_MS;
      const noSpeech = !heard && now - t0 > NO_SPEECH_MS;
      if (speechEnded || tooLong || noSpeech) { try { if (mr.state !== "inactive") mr.stop(); } catch {} return; }
      requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  });

  const convoLoop = async () => {
    const stream = vadStreamRef.current;
    const ctx = vadCtxRef.current;
    if (!stream || !ctx) return;
    const src = ctx.createMediaStreamSource(stream);
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 1024;
    src.connect(analyser);
    const buf = new Float32Array(analyser.fftSize);
    const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
    while (convoModeRef.current) {
      // Don't listen while Jarvis is thinking or speaking (avoid echo).
      while (convoModeRef.current && (speakingRef.current || busyRef.current)) await sleep(180);
      if (!convoModeRef.current) break;
      const blob = await recordUtterance(stream, analyser, buf);
      setRecording(false);
      if (!convoModeRef.current) break;
      if (blob && blob.size > 1400) {
        setBusy(true);
        try {
          const fd = new FormData();
          fd.append("audio", blob, "audio.webm");
          const { data } = await axios.post(`${API}/doserne/jarvis/transcribe`, fd, authHeader());
          const text = (data.text || "").trim();
          setBusy(false);
          if (text) await send(text);
        } catch { setBusy(false); }
      }
      await sleep(250);
    }
  };

  const stopConvo = () => {
    convoModeRef.current = false;
    setConvoMode(false);
    setRecording(false);
    try { if (vadStreamRef.current) vadStreamRef.current.getTracks().forEach((t) => t.stop()); } catch {}
    vadStreamRef.current = null;
    try { if (vadCtxRef.current) vadCtxRef.current.close(); } catch {}
    vadCtxRef.current = null;
  };

  const startConvo = async () => {
    if (convoModeRef.current) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true } });
      vadStreamRef.current = stream;
      const Ctx = window.AudioContext || window.webkitAudioContext;
      vadCtxRef.current = new Ctx();
      try { await vadCtxRef.current.resume(); } catch {}
      convoModeRef.current = true;
      setConvoMode(true);
      setOpen(true);
      convoLoop();
    } catch {
      setMsgs((m) => [...m, { role: "assistant", text: "⚠️ Nu am acces la microfon.", error: true }]);
    }
  };
  // ---- SPACEBAR WAKE WORD — hold Space anywhere → listen → if the admin says
  // "wake up jarvis" (or Romanian / Norwegian variants) → navigate home + open HUD + start voice convo.
  // Otherwise the audio is discarded (we don't want random utterances in the chat).
  const pttMrRef = useRef(null);
  const pttStreamRef = useRef(null);
  const pttActiveRef = useRef(false);
  const [pttOn, setPttOn] = useState(false);

  const WAKE_PATTERNS = [
    /\bwake\s*up\s*jarvis\b/i,
    /\bjarvis\s*wake\s*up\b/i,
    /\btreze(?:ș|s)te[-\s]?te\s*jarvis\b/i,
    /\bjarvis\s*treze(?:ș|s)te[-\s]?te\b/i,
    /\bv(?:å|a)kne\s*opp\s*jarvis\b/i,
    /\bhey\s*jarvis\b/i,
    /\bok\s*jarvis\b/i,
  ];
  const isWakePhrase = (txt) => {
    const s = (txt || "").toLowerCase().trim();
    if (!s) return false;
    return WAKE_PATTERNS.some((rx) => rx.test(s));
  };

  const stopPtt = async () => {
    const mr = pttMrRef.current;
    if (!mr) return;
    try { if (mr.state !== "inactive") mr.stop(); } catch { /* ignore */ }
  };

  const startPtt = async () => {
    if (pttActiveRef.current || convoModeRef.current || busyRef.current) return;
    pttActiveRef.current = true;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true } });
      pttStreamRef.current = stream;
      const mime = MediaRecorder.isTypeSupported("audio/webm;codecs=opus") ? "audio/webm;codecs=opus" : "audio/webm";
      const mr = new MediaRecorder(stream, { mimeType: mime });
      pttMrRef.current = mr;
      const chunks = [];
      mr.ondataavailable = (ev) => { if (ev.data.size) chunks.push(ev.data); };
      mr.onstop = async () => {
        setPttOn(false);
        try { stream.getTracks().forEach((t) => t.stop()); } catch { /* ignore */ }
        pttMrRef.current = null; pttStreamRef.current = null; pttActiveRef.current = false;
        if (!chunks.length) return;
        const blob = new Blob(chunks, { type: "audio/webm" });
        if (blob.size < 1200) return; // too short
        try {
          const fd = new FormData();
          fd.append("audio", blob, "audio.webm");
          const { data } = await axios.post(`${API}/doserne/jarvis/transcribe`, fd, authHeader());
          const text = (data.text || "").trim();
          if (!text) return;
          if (!isWakePhrase(text)) return;  // not the wake word — silently ignore
          // Wake triggered → go home + open Jarvis HUD + enter voice conversation.
          try { navigate("/doserne"); } catch { window.location.assign("/doserne"); }
          setOpen(true); setActive(true);
          setTimeout(() => { try { startConvo(); } catch { /* ignore */ } }, 500);
        } catch { /* ignore transcription failures */ }
      };
      mr.start();
      setPttOn(true);
    } catch {
      pttActiveRef.current = false;
    }
  };

  // Global keyboard: hold Space outside form fields to push-to-talk.
  useEffect(() => {
    const isTypingTarget = (el) => {
      if (!el) return false;
      const t = (el.tagName || "").toLowerCase();
      if (t === "input" || t === "textarea" || t === "select") return true;
      return !!el.isContentEditable;
    };
    const onDown = (e) => {
      if (e.key !== " " && e.code !== "Space") return;
      if (e.repeat) return;
      if (isTypingTarget(e.target)) return;
      if (convoModeRef.current || busyRef.current || pttActiveRef.current) return;
      e.preventDefault();
      startPtt();
    };
    const onUp = (e) => {
      if (e.key !== " " && e.code !== "Space") return;
      if (!pttActiveRef.current) return;
      e.preventDefault();
      stopPtt();
    };
    window.addEventListener("keydown", onDown);
    window.addEventListener("keyup", onUp);
    return () => {
      window.removeEventListener("keydown", onDown);
      window.removeEventListener("keyup", onUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleConvo = () => { if (convoModeRef.current) stopConvo(); else startConvo(); };

  // Open the panel. If Jarvis is already awake (has today's memory), just show
  // the panel and CONTINUE the conversation — no re-boot, no repeated briefing.
  const openJarvis = () => {
    // Click = open the chat only. Never trigger boot / briefing / HUD takeover.
    // Both the full HUD and the morning boot are reserved for the voice
    // wake-word ("Wake up Jarvis", SPACE).
    setOpen(true); setAwake(true);
  };
  // Minimize to the floating bubble — KEEPS the conversation + memory.
  const minimize = () => { stopRec(); stopConvo(); stopJarvis(); setOpen(false); setActive(false); };
  // Full shutdown — clears today's memory; next open runs the boot + briefing.
  const shutdown = () => {
    stopRec(); stopConvo(); stopJarvis();
    setOpen(false); setActive(false);
    setMsgs([]); setAwake(false);
    setPendingForm(null); setFormVals({});
    try { localStorage.removeItem(MSGS_KEY); localStorage.removeItem(SESSION_KEY); } catch {}
    sessionId.current = uid();
    try { localStorage.setItem(SESSION_KEY, sessionId.current); } catch {}
  };


  return (
    <>
      <style>{`
        @keyframes jv-scan { 0%{transform:translateY(-100%)} 100%{transform:translateY(2000%)} }
        @keyframes jv-spin { to { transform: rotate(360deg) } }
        @keyframes jv-spin-r { to { transform: rotate(-360deg) } }
        @keyframes jv-flick { 0%,100%{opacity:.35} 50%{opacity:1} }
        @keyframes jv-eq { 0%,100%{transform:scaleY(.28)} 50%{transform:scaleY(1)} }
        @keyframes jv-halo { 0%,100%{opacity:.5;box-shadow:0 0 22px 2px rgba(34,211,238,.35)} 50%{opacity:1;box-shadow:0 0 40px 6px rgba(34,211,238,.6)} }
      `}</style>

      {/* Quiet new-email notification — sender only; body stays private until approved */}
      {mailAlert && (
        <div
          className="fixed right-5 bottom-24 z-[92] w-[min(92vw,340px)] rounded-2xl overflow-hidden shadow-2xl border border-cyan-400/30 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-cyan-100 animate-[jv-halo_2.4s_ease-in-out_infinite]"
          data-testid="jarvis-mail-alert"
        >
          <div className="px-4 pt-3 pb-2 flex items-start gap-3">
            <div className="mt-0.5 w-9 h-9 rounded-full bg-cyan-500/15 border border-cyan-400/30 flex items-center justify-center text-cyan-300 text-lg">✉</div>
            <div className="min-w-0 flex-1">
              <div className="text-[11px] uppercase tracking-wider text-cyan-400/70">New email</div>
              <div className="text-sm font-semibold truncate" data-testid="jarvis-mail-alert-from">{mailAlert.from || mailAlert.from_addr || "Unknown sender"}</div>
              <div className="text-xs text-cyan-200/60 truncate">{mailAlert.subject || ""}</div>
            </div>
          </div>
          <div className="flex border-t border-cyan-400/15">
            <button
              onClick={() => readLatestEmail(mailAlert.from_addr)}
              data-testid="jarvis-mail-alert-read"
              className="flex-1 py-2.5 text-sm font-semibold text-cyan-300 hover:bg-cyan-400/10 active:bg-cyan-400/20 transition-colors"
            >Read it</button>
            <button
              onClick={dismissMailAlert}
              data-testid="jarvis-mail-alert-dismiss"
              className="flex-1 py-2.5 text-sm text-slate-400 border-l border-cyan-400/15 hover:bg-white/5 active:bg-white/10 transition-colors"
            >Not now</button>
          </div>
        </div>
      )}

      {/* Guided tour → cinematic voice HUD (no chat panel while presenting) */}
      {tour && (
        <div
          className="fixed top-4 left-1/2 -translate-x-1/2 z-[95] w-[min(92vw,560px)] flex flex-col items-center"
          data-testid="jarvis-tour-bar"
        >
          <div
            className="w-full rounded-2xl px-4 py-3 border border-cyan-400/40 bg-slate-950/85 backdrop-blur-md flex items-center gap-3"
            style={{ animation: "jv-halo 2.4s ease-in-out infinite" }}
          >
            {/* voice equalizer */}
            <div className="flex items-end gap-[3px] h-7 shrink-0" aria-hidden="true">
              {[0, 1, 2, 3, 4, 5, 6].map((b) => (
                <span
                  key={b}
                  className="w-[3px] rounded-full bg-cyan-300"
                  style={{
                    height: "100%",
                    transformOrigin: "bottom",
                    animation: `jv-eq ${0.62 + (b % 4) * 0.13}s ease-in-out ${b * 0.09}s infinite`,
                    opacity: speaking ? 1 : 0.4,
                    boxShadow: "0 0 8px rgba(34,211,238,.7)",
                  }}
                />
              ))}
            </div>
            {/* label + caption */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-cyan-300 text-[13px] font-black tracking-[0.22em]" style={{ animation: "jv-flick 2.2s ease-in-out infinite" }}>
                  JARVIS
                </span>
                <span className="text-cyan-500/70 text-[11px] font-mono">
                  {String(tour.index + 1).padStart(2, "0")}/{String(tour.steps.length).padStart(2, "0")}
                </span>
              </div>
              <div
                className="text-cyan-100/90 text-[12px] leading-snug mt-0.5 line-clamp-2"
                data-testid="jarvis-tour-caption"
              >
                {tour.caption}
              </div>
            </div>
            {/* stop */}
            <button
              onClick={stopTour}
              data-testid="jarvis-tour-stop"
              className="shrink-0 self-start px-3 py-1.5 rounded-full bg-rose-500/90 hover:bg-rose-500 text-white text-xs font-bold transition-colors"
            >
              Stop
            </button>
          </div>
        </div>
      )}

      {/* Confidential Email (IMAP) connect modal */}
      {mailModal && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" data-testid="jarvis-mail-modal" onClick={() => setMailModal(false)}>
          <div className="w-full max-w-sm rounded-3xl overflow-hidden bg-slate-950 border border-cyan-900/50 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="relative px-6 pt-7 pb-5 bg-gradient-to-b from-cyan-950/60 to-slate-950 text-center">
              <button type="button" onClick={() => setMailModal(false)} className="absolute top-3 right-3 p-1.5 rounded-full text-slate-400 hover:bg-white/10"><X className="w-4 h-4" /></button>
              <div className="mx-auto w-16 h-16 rounded-2xl bg-cyan-400/10 border border-cyan-400/40 flex items-center justify-center mb-3">
                <ShieldCheck className="w-8 h-8 text-cyan-300" />
              </div>
              <h3 className="text-white font-black text-lg flex items-center justify-center gap-1.5"><Lock className="w-4 h-4 text-cyan-300" /> Conexiune privată email</h3>
              <p className="text-slate-400 text-xs mt-1.5 leading-relaxed">Îți conectezi căsuța ta de email a firmei. Totul e strict confidențial — nimeni altcineva nu-ți vede emailurile.</p>
            </div>

            <div className="px-6 py-4 space-y-2.5">
              {[
                "Doar TU vezi căsuța ta — fiecare admin are contul lui, izolat.",
                "Conexiune securizată IMAP/SSL — direct la serverul firmei.",
                "Parola e criptată și stocată securizat. Fără copii ale emailurilor.",
                "Poți deconecta oricând, dintr-un singur click.",
              ].map((t, i) => (
                <div key={i} className="flex items-start gap-2.5">
                  <div className="mt-0.5 w-5 h-5 rounded-full bg-emerald-500/15 border border-emerald-500/40 flex items-center justify-center flex-shrink-0"><Check className="w-3 h-3 text-emerald-400" /></div>
                  <span className="text-slate-300 text-[13px] leading-snug">{t}</span>
                </div>
              ))}
            </div>

            <div className="px-6 pb-6 pt-1">
              {mail.connected ? (
                <>
                  <div className="text-center text-emerald-300 text-sm font-bold mb-3 flex items-center justify-center gap-1.5">
                    <ShieldCheck className="w-4 h-4" /> Conectat privat{mail.email ? `: ${mail.email}` : ""}
                  </div>
                  <button type="button" onClick={disconnectMail} data-testid="jarvis-mail-disconnect"
                    className="w-full py-2.5 rounded-xl border border-red-500/40 text-red-300 hover:bg-red-500/10 text-sm font-bold">
                    Deconectează contul
                  </button>
                </>
              ) : (
                <div className="space-y-2.5">
                  <input type="email" value={mailForm.email} onChange={(e) => setMailForm((f) => ({ ...f, email: e.target.value }))}
                    placeholder="adresa@firma.com" autoComplete="username" data-testid="jarvis-mail-email"
                    className="w-full bg-slate-900 border border-cyan-900/50 text-slate-100 placeholder-slate-500 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500" />
                  <input type="password" value={mailForm.password} onChange={(e) => setMailForm((f) => ({ ...f, password: e.target.value }))}
                    onKeyDown={(e) => e.key === "Enter" && connectMail()}
                    placeholder="parola căsuței de email" autoComplete="current-password" data-testid="jarvis-mail-password"
                    className="w-full bg-slate-900 border border-cyan-900/50 text-slate-100 placeholder-slate-500 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500" />
                  {mailErr && <div className="text-red-300 text-xs bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2" data-testid="jarvis-mail-error">{mailErr}</div>}
                  <button type="button" onClick={connectMail} disabled={mailBusy} data-testid="jarvis-mail-connect"
                    className="w-full py-3 rounded-xl bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 disabled:opacity-50 text-white text-sm font-black flex items-center justify-center gap-2 shadow-lg shadow-cyan-900/40">
                    {mailBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />} {mailBusy ? "Verific…" : "Conectează-te securizat"}
                  </button>
                </div>
              )}
              <p className="text-center text-slate-600 text-[10px] mt-3 flex items-center justify-center gap-1"><Lock className="w-3 h-3" /> Criptat • Privat • Doar pentru tine</p>
            </div>
          </div>
        </div>
      )}

      {!open && (
        <button type="button" onClick={openJarvis} data-testid="jarvis-fab"
          className="fixed left-5 bottom-5 z-[60] flex items-center gap-2 pl-3 pr-4 h-14 rounded-full shadow-xl shadow-cyan-900/40 border border-cyan-400/30 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-cyan-300 hover:scale-105 active:scale-95 transition-transform"
          title="Wake up Jarvis">
          <span className="relative w-9 h-9 flex items-center justify-center">
            <span className="absolute inset-0 rounded-full bg-cyan-400/20" style={{ animation: "jv-flick 2s infinite" }} />
            <span className="absolute inset-1 rounded-full border-2 border-cyan-400/60" />
            <Bot className="w-5 h-5 relative" />
          </span>
          <span className="text-xs font-black uppercase tracking-widest">Wake up Jarvis</span>
        </button>
      )}

      {open && (
        <div data-testid="jarvis-panel"
          style={{ width: `min(92vw, ${panelSize.w}px)`, height: `min(92vh, ${panelSize.h}px)` }}
          className="fixed left-4 bottom-4 z-[60] rounded-3xl overflow-hidden shadow-2xl border border-cyan-900/50 bg-slate-950 flex flex-col">
          {/* Resize handle — drag from the top-right corner to resize. */}
          <div onMouseDown={startResize} data-testid="jarvis-resize"
            title="Trage pentru a redimensiona"
            className="absolute top-0 right-0 w-5 h-5 z-[70] cursor-nesw-resize flex items-start justify-end p-1 group">
            <span className="w-2.5 h-2.5 border-t-2 border-r-2 border-cyan-400/40 group-hover:border-cyan-300 transition" />
          </div>

          {/* boot overlay — cinematic sci-fi HUD */}
          {booting && (
            <div className="absolute inset-0 z-20 bg-slate-950 flex flex-col items-center justify-center overflow-hidden" data-testid="jarvis-boot">
              {/* scanning line */}
              <div className="absolute left-0 right-0 h-16 bg-gradient-to-b from-cyan-400/25 to-transparent" style={{ animation: "jv-scan 1.6s linear infinite" }} />
              {/* grid backdrop */}
              <div className="absolute inset-0 opacity-20" style={{ backgroundImage: "linear-gradient(rgba(34,211,238,.25) 1px,transparent 1px),linear-gradient(90deg,rgba(34,211,238,.25) 1px,transparent 1px)", backgroundSize: "26px 26px" }} />
              {/* scanning app thumbnails */}
              {bootData.images && bootData.images.length > 0 && (
                <div className="absolute top-4 left-0 right-0 flex gap-2 justify-center px-3 opacity-80">
                  {bootData.images.slice(0, 5).map((src, i) => (
                    <div key={i} className="w-12 h-12 rounded-md overflow-hidden border border-cyan-400/50" style={{ animation: `jv-flick ${1 + i * 0.25}s infinite` }}>
                      <img src={src} alt="" className="w-full h-full object-cover" style={{ filter: "hue-rotate(150deg) saturate(1.4) brightness(.9)" }} />
                    </div>
                  ))}
                </div>
              )}
              {/* arc reactor */}
              <div className="relative w-40 h-40 flex items-center justify-center">
                <div className="absolute inset-0 rounded-full border-2 border-cyan-400/30" style={{ animation: "jv-spin 3s linear infinite" }} />
                <div className="absolute inset-2 rounded-full border border-cyan-300/40 border-dashed" style={{ animation: "jv-spin-r 4s linear infinite" }} />
                <div className="absolute inset-8 rounded-full border-2 border-cyan-400/50" style={{ animation: "jv-spin 2s linear infinite" }} />
                <div className="absolute inset-0 rounded-full border-t-2 border-fuchsia-400/60" style={{ animation: "jv-spin 1.2s linear infinite" }} />
                <div className="w-16 h-16 rounded-full bg-cyan-400/90 blur-[2px]" style={{ animation: "jv-flick 1s infinite", boxShadow: "0 0 46px 12px rgba(34,211,238,.65)" }} />
                <Bot className="w-8 h-8 text-slate-900 absolute" />
              </div>
              {/* live stat readouts */}
              {bootData.stats && (
                <div className="mt-4 grid grid-cols-3 gap-2 font-mono text-center" data-testid="jarvis-boot-stats">
                  {[["PROIECTE", bootData.stats.projects], ["ECHIPĂ", bootData.stats.members], ["SEMNATE", bootData.stats.signed]].map(([k, v]) => (
                    <div key={k} className="px-2 py-1 rounded border border-cyan-500/30 bg-cyan-500/5">
                      <div className="text-cyan-300 text-lg font-black leading-none">{bootStep >= 2 ? v : "--"}</div>
                      <div className="text-[8px] text-cyan-500/70 tracking-widest mt-0.5">{k}</div>
                    </div>
                  ))}
                </div>
              )}
              <div className="mt-4 font-mono text-cyan-300 text-sm tracking-widest" data-testid="jarvis-boot-line">
                {BOOT_LINES[Math.min(bootStep, BOOT_LINES.length - 1)]}<span className="animate-pulse">_</span>
              </div>
              <div className="mt-2 flex gap-1">
                {BOOT_LINES.map((_, i) => (
                  <span key={i} className={`w-8 h-1 rounded-full ${i <= bootStep ? "bg-cyan-400" : "bg-slate-700"}`} />
                ))}
              </div>
              <div className="mt-5 flex items-center gap-2">
                <button type="button" onClick={() => finishBootRef.current && finishBootRef.current()} data-testid="jarvis-boot-skip"
                  className="px-3 py-1.5 rounded-full border border-cyan-500/40 text-cyan-300 text-xs font-bold hover:bg-cyan-500/10">
                  Skip ⏭
                </button>
                <button type="button" onClick={stopJarvis} data-testid="jarvis-boot-stop"
                  className="px-3 py-1.5 rounded-full border border-rose-500/40 text-rose-300 text-xs font-bold hover:bg-rose-500/10 flex items-center gap-1">
                  <Square className="w-3 h-3" /> Oprește
                </button>
              </div>
            </div>
          )}

          {/* header — Messenger-style: avatar + name/status + settings popover */}
          <div className="relative flex items-center gap-3 px-4 py-3 bg-slate-950 text-white border-b border-white/5">
            <div className="relative shrink-0">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center shadow-lg shadow-cyan-500/20">
                <Bot className="w-5 h-5 text-white" />
              </div>
              <span className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-slate-950 ${busy || recording || pttOn ? "bg-amber-400 animate-pulse" : "bg-emerald-400"}`} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-bold text-[15px] leading-tight truncate">J.A.R.V.I.S</div>
              <div className="text-[11px] text-slate-400 truncate">
                {pttOn ? "ascult wake word…" : convoMode ? "conversație activă" : recording ? "ascult…" : busy ? "procesez…" : "online"}
              </div>
            </div>
            {(booting || speaking) && (
              <button type="button" onClick={stopJarvis} data-testid="jarvis-stop"
                className="w-9 h-9 rounded-full hover:bg-white/5 flex items-center justify-center text-rose-400" title="Oprește / Întrerupe">
                <Square className="w-4 h-4" />
              </button>
            )}
            <button type="button" onClick={() => setSettingsOpen((v) => !v)} data-testid="jarvis-settings"
              className={`w-9 h-9 rounded-full flex items-center justify-center transition ${settingsOpen ? "bg-cyan-500/20 text-cyan-300" : "hover:bg-white/5 text-slate-400"}`} title="Setări">
              <SettingsIcon className="w-4 h-4" />
            </button>
            <button type="button" onClick={minimize} data-testid="jarvis-close"
              className="w-9 h-9 rounded-full hover:bg-cyan-500/15 flex items-center justify-center text-slate-300 hover:text-cyan-300 transition" title="Minimizează — explorează platforma (conversația se păstrează)">
              <Minus className="w-4 h-4" />
            </button>

            {settingsOpen && (
              <>
                <div className="fixed inset-0 z-[60]" onClick={() => setSettingsOpen(false)} />
                <div className="absolute right-3 top-[58px] z-[61] w-72 bg-slate-900 border border-white/10 rounded-xl shadow-2xl shadow-black/50 overflow-hidden" onClick={(e) => e.stopPropagation()}>
                  <div className="px-4 py-3 border-b border-white/5">
                    <div className="text-[10px] font-black uppercase tracking-widest text-cyan-400">Setări Jarvis</div>
                    <div className="text-[10px] text-slate-500 mt-0.5">Ține <kbd className="px-1 py-0.5 rounded bg-white/5 text-slate-300 text-[9px] font-bold">SPACE</kbd> și spune „Wake up Jarvis"</div>
                  </div>
                  <div className="py-1.5">
                    <button type="button" onClick={() => { setMailModal(true); setSettingsOpen(false); }}
                      className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-white/5 text-left transition" data-testid="jarvis-mail">
                      <Mail className={`w-4 h-4 ${mail.connected ? "text-emerald-400" : "text-slate-400"}`} />
                      <div className="flex-1">
                        <div className="text-[13px] font-bold text-slate-200">Email</div>
                        <div className="text-[10px] text-slate-500 truncate">{mail.connected ? mail.email : "Conectează-ți inbox-ul"}</div>
                      </div>
                      {mail.connected && <Check className="w-3.5 h-3.5 text-emerald-400" />}
                    </button>
                    <button type="button" onClick={() => setVoiceOn((v) => !v)}
                      className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-white/5 text-left transition" data-testid="jarvis-voice-toggle">
                      {voiceOn ? <Volume2 className="w-4 h-4 text-cyan-400" /> : <VolumeX className="w-4 h-4 text-slate-500" />}
                      <div className="flex-1">
                        <div className="text-[13px] font-bold text-slate-200">Voce</div>
                        <div className="text-[10px] text-slate-500">{voiceOn ? "Jarvis răspunde vocal" : "Fără voce — doar text"}</div>
                      </div>
                      <span className={`relative w-8 h-4 rounded-full transition ${voiceOn ? "bg-cyan-500" : "bg-slate-700"}`}>
                        <span className={`absolute top-0.5 w-3 h-3 rounded-full bg-white transition-all ${voiceOn ? "left-[18px]" : "left-0.5"}`} />
                      </span>
                    </button>
                    <button type="button" onClick={toggleNotify}
                      className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-white/5 text-left transition" data-testid="jarvis-notify-toggle">
                      {notifyOn ? <Bell className="w-4 h-4 text-cyan-400" /> : <BellOff className="w-4 h-4 text-amber-400" />}
                      <div className="flex-1">
                        <div className="text-[13px] font-bold text-slate-200">Alerte email / mesaje</div>
                        <div className="text-[10px] text-slate-500">{notifyOn ? "Jarvis anunță email-urile noi" : "Fără anunțuri audio"}</div>
                      </div>
                      <span className={`relative w-8 h-4 rounded-full transition ${notifyOn ? "bg-cyan-500" : "bg-slate-700"}`}>
                        <span className={`absolute top-0.5 w-3 h-3 rounded-full bg-white transition-all ${notifyOn ? "left-[18px]" : "left-0.5"}`} />
                      </span>
                    </button>
                    <div className="px-4 pt-2 pb-1.5 border-t border-white/5 mt-1">
                      <div className="text-[10px] font-black uppercase tracking-wider text-slate-500 mb-1.5">Vocea</div>
                      <select value={voice} onChange={(e) => pickVoice(e.target.value)} data-testid="jarvis-voice-select"
                        className="w-full text-[12px] bg-slate-800 text-slate-200 border border-white/10 rounded-lg px-2.5 py-2 outline-none hover:bg-slate-700/70 cursor-pointer font-bold">
                        {VOICE_OPTIONS.map((o) => (<option key={o.id} value={o.id}>{o.label}</option>))}
                      </select>
                    </div>
                    <button type="button" onClick={() => { shutdown(); setSettingsOpen(false); }} data-testid="jarvis-shutdown"
                      className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-rose-500/10 text-left transition border-t border-white/5 mt-1">
                      <Power className="w-4 h-4 text-rose-400" />
                      <div className="flex-1">
                        <div className="text-[13px] font-bold text-rose-300">Resetare sesiune</div>
                        <div className="text-[10px] text-slate-500">Uită conversația și repornește briefingul</div>
                      </div>
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 py-4 space-y-3 bg-slate-950">
            {msgs.length === 0 && !booting && (
              <div className="text-center text-slate-500 text-sm mt-10 px-6">{IDLE[L]}</div>
            )}
            {msgs.map((m, i) => {
              const isUser = m.role === "user";
              const showAvatar = !isUser && (i === 0 || msgs[i - 1]?.role === "user");
              return (
              <div key={i} data-testid="jarvis-message" className={`flex items-end gap-2 ${isUser ? "justify-end" : "justify-start"}`}>
                {!isUser && (
                  showAvatar ? (
                    <div className="w-7 h-7 shrink-0 rounded-full bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center shadow-md shadow-cyan-500/20 ring-1 ring-cyan-400/30">
                      <Bot className="w-4 h-4 text-white" />
                    </div>
                  ) : (
                    <div className="w-7 shrink-0" aria-hidden />
                  )
                )}
                <div className={`max-w-[85%] px-4 py-2.5 rounded-2xl text-[14px] leading-relaxed whitespace-pre-wrap shadow-md ${
                  isUser
                    ? "bg-gradient-to-br from-cyan-500 to-cyan-700 text-white rounded-br-sm"
                    : m.error
                      ? "bg-rose-950/80 text-rose-200 border border-rose-800/60 rounded-bl-sm"
                      : "bg-slate-800/80 text-slate-100 rounded-bl-sm border border-cyan-700/20 backdrop-blur"}`}>
                  {m.tools && m.tools.length > 0 && (
                    <div className="mb-2 flex flex-wrap gap-1">
                      {m.tools.map((tl, j) => {
                        const toolName = typeof tl === "string" ? tl : tl.name;
                        const status = typeof tl === "string" ? "ok" : (tl.status || "ok");
                        const color = status === "running"
                          ? "bg-amber-500/15 text-amber-300 border-amber-500/40"
                          : status === "error"
                            ? "bg-rose-500/15 text-rose-300 border-rose-500/40"
                            : "bg-cyan-500/15 text-cyan-300 border-cyan-500/40";
                        return (
                          <span key={j} className={`inline-flex items-center gap-1 text-[9.5px] uppercase tracking-wider font-black px-1.5 py-0.5 rounded border ${color}`}>
                            {status === "running" ? <Loader2 className="w-2.5 h-2.5 animate-spin" /> : status === "error" ? <X className="w-2.5 h-2.5" /> : <Check className="w-2.5 h-2.5" />}
                            {toolName}
                          </span>
                        );
                      })}
                    </div>
                  )}
                  {m.text || (m.streaming ? "" : "…")}
                  {m.streaming && <span className="inline-block w-1.5 h-4 ml-0.5 align-text-bottom bg-cyan-300/80 animate-pulse" />}
                  {m.role === "assistant" && !m.error && !m.streaming && (m.text || "").trim() && (
                    <button type="button" onClick={() => copyMsg(m.text, i)} data-testid="jarvis-copy-msg"
                      title="Copiază" className="ml-2 align-middle inline-flex items-center gap-1 text-[10px] text-cyan-400/70 hover:text-cyan-200">
                      {copiedIdx === i ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                    </button>
                  )}
                  {m.image && (
                    <img src={m.image} alt="măsurători fațade" data-testid="jarvis-measure-image"
                      className="mt-2 rounded-lg border border-cyan-900/40 w-full cursor-zoom-in"
                      onClick={() => openLightbox(m.image)} />
                  )}
                </div>
              </div>
              );
            })}
            {busy && msgs[msgs.length - 1]?.streaming !== true && (
              <div className="flex items-end gap-2 justify-start">
                <div className="w-7 h-7 shrink-0 rounded-full bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center shadow-md shadow-cyan-500/20 ring-1 ring-cyan-400/30">
                  <Bot className="w-4 h-4 text-white" />
                </div>
                <div className="px-3.5 py-2.5 rounded-2xl bg-slate-800/80 text-cyan-300 border border-cyan-900/30 backdrop-blur rounded-bl-sm">
                  <Loader2 className="w-4 h-4 animate-spin" />
                </div>
              </div>
            )}
          </div>

          {/* Jarvis-requested form */}
          {pendingForm && (
            <div data-testid="jarvis-form" className="px-3 py-3 bg-slate-900 border-t border-cyan-700/50 max-h-[42vh] overflow-y-auto">
              <div className="flex items-center gap-2 mb-2.5">
                <Sparkles className="w-4 h-4 text-cyan-400" />
                <span className="text-sm font-bold text-cyan-200">{pendingForm.title}</span>
                <button type="button" onClick={() => { setPendingForm(null); setFormVals({}); }} data-testid="jarvis-form-cancel"
                  className="ml-auto text-slate-400 hover:text-white p-1 rounded-full hover:bg-white/10"><X className="w-4 h-4" /></button>
              </div>
              <div className="space-y-2.5">
                {pendingForm.fields.map((fl) => (
                  <div key={fl.name}>
                    <label className="block text-[11px] font-semibold text-slate-300 mb-1">{fl.label}{fl.required && <span className="text-rose-400"> *</span>}</label>
                    {fl.type === "textarea" ? (
                      <textarea value={formVals[fl.name] || ""} rows={3}
                        onChange={(e) => setFormVals((v) => ({ ...v, [fl.name]: e.target.value }))}
                        placeholder={fl.placeholder || ""} data-testid={`jarvis-form-${fl.name}`}
                        className="w-full bg-slate-800 text-slate-100 placeholder-slate-500 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 resize-none" />
                    ) : (
                      <input type={["number", "date", "email", "tel"].includes(fl.type) ? fl.type : "text"}
                        value={formVals[fl.name] || ""}
                        onChange={(e) => setFormVals((v) => ({ ...v, [fl.name]: e.target.value }))}
                        onKeyDown={(e) => e.key === "Enter" && submitForm()}
                        placeholder={fl.placeholder || ""} data-testid={`jarvis-form-${fl.name}`}
                        className="w-full bg-slate-800 text-slate-100 placeholder-slate-500 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500" />
                    )}
                  </div>
                ))}
              </div>
              <button type="button" onClick={submitForm} disabled={busy} data-testid="jarvis-form-submit"
                className="mt-3 w-full bg-cyan-600 hover:bg-cyan-500 disabled:opacity-40 text-white font-bold rounded-lg py-2.5 text-sm flex items-center justify-center gap-2">
                <Check className="w-4 h-4" /> Trimite lui Jarvis
              </button>
            </div>
          )}

          {/* Facade measurement — calibration reference bar */}
          {measureRef && (
            <div data-testid="jarvis-measure-ref" className="px-3 py-3 bg-slate-900 border-t border-amber-600/40">
              <div className="flex items-center gap-2 mb-2">
                <Ruler className="w-4 h-4 text-amber-400" />
                <span className="text-xs font-bold text-amber-200">Calibrare — dă un reper real pentru „{measureRef.name}"</span>
                <button type="button" onClick={() => setMeasureRef(null)} className="ml-auto text-slate-400 hover:text-white p-1 rounded-full hover:bg-white/10"><X className="w-4 h-4" /></button>
              </div>
              <div className="flex gap-2">
                <input value={measureRefInput} onChange={(e) => setMeasureRefInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && measureRefInput.trim()) { measureFacades(measureFileRef.current, measureRefInput.trim()); setMeasureRefInput(""); } }}
                  placeholder='ex: „ușa = 2,1 m" sau „lățime sud = 9 m"' data-testid="jarvis-measure-ref-input"
                  className="flex-1 bg-slate-800 text-slate-100 placeholder-slate-500 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500" />
                <button type="button" disabled={busy || !measureRefInput.trim()} data-testid="jarvis-measure-recalc"
                  onClick={() => { measureFacades(measureFileRef.current, measureRefInput.trim()); setMeasureRefInput(""); }}
                  className="bg-amber-500 hover:bg-amber-400 disabled:opacity-40 text-slate-900 font-bold rounded-lg px-3 text-sm">Recalculează</button>
              </div>
            </div>
          )}

          {/* input */}
          <div className="px-3 py-3 bg-slate-900 border-t border-cyan-900/50 flex flex-col gap-2" onPaste={handlePaste}>
            {pendingFiles.length > 0 && (
              <div className="flex flex-wrap gap-2" data-testid="jarvis-pending-files">
                {pendingFiles.map((f, i) => (
                  <div key={`${f.name}-${i}`} className="relative flex items-center gap-1.5 bg-slate-800 border border-cyan-900/50 rounded-lg pl-1.5 pr-6 py-1 max-w-[170px]" data-testid={`jarvis-pending-${i}`}>
                    {(f.type || "").startsWith("image/") ? (
                      <img src={URL.createObjectURL(f)} alt="" className="w-7 h-7 rounded object-cover flex-shrink-0" />
                    ) : (
                      <Paperclip className="w-4 h-4 text-cyan-300 flex-shrink-0" />
                    )}
                    <span className="text-[11px] text-cyan-100 truncate">{f.name}</span>
                    <button type="button" onClick={() => removePending(i)} data-testid={`jarvis-pending-remove-${i}`}
                      className="absolute right-1 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white">
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <div className="flex items-center gap-2">
            <button type="button" onClick={toggleConvo} data-testid="jarvis-mic"
              className={`w-11 h-11 flex-shrink-0 rounded-full flex items-center justify-center transition-colors ${convoMode ? "bg-red-600 text-white animate-pulse" : "bg-slate-800 text-cyan-300 hover:bg-slate-700"}`}
              title={convoMode ? "Oprește conversația (hands-free)" : "Pornește conversația continuă — vorbește liber până oprești"}>
              {convoMode ? <Square className="w-4 h-4" /> : <Mic className="w-5 h-5" />}
            </button>
            <input ref={fileInputRef} type="file" accept="image/*,application/pdf" multiple onChange={analyzeFile} className="hidden" data-testid="jarvis-file-input" />
            <button type="button" onClick={() => fileInputRef.current && fileInputRef.current.click()} disabled={busy}
              data-testid="jarvis-attach"
              className="w-11 h-11 flex-shrink-0 rounded-full bg-slate-800 text-cyan-300 hover:bg-slate-700 disabled:opacity-40 flex items-center justify-center"
              title="Atașează o poză sau un PDF — se trimite împreună cu mesajul tău">
              <Paperclip className="w-5 h-5" />
            </button>
            <input ref={measureInputRef} type="file" accept="image/*,application/pdf,.ifc" multiple onChange={(e) => { const fs = Array.from(e.target.files || []); if (fs.length) measureFacades(fs); e.target.value = ""; }} className="hidden" data-testid="jarvis-measure-input" />
            <button type="button" onClick={() => measureInputRef.current && measureInputRef.current.click()} disabled={busy}
              data-testid="jarvis-measure-btn"
              className="w-11 h-11 flex-shrink-0 rounded-full bg-slate-800 text-amber-300 hover:bg-slate-700 disabled:opacity-40 flex items-center justify-center"
              title="Măsoară m² pe fațadă dintr-un desen PDF/imagine sau model IFC">
              <Ruler className="w-5 h-5" />
            </button>
            <input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && submitComposer()} onPaste={handlePaste}
              placeholder={pendingFiles.length ? "Scrie ce să fac cu fișierul, apoi trimite…" : "Scrie, lipește o poză (Ctrl+V) sau apasă microfonul…"} data-testid="jarvis-input"
              className="flex-1 bg-slate-800 text-slate-100 placeholder-slate-500 rounded-full px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500" />
            <button type="button" onClick={submitComposer} disabled={busy || (!input.trim() && !pendingFiles.length)} data-testid="jarvis-send"
              className="w-11 h-11 flex-shrink-0 rounded-full bg-cyan-600 hover:bg-cyan-500 disabled:opacity-40 text-white flex items-center justify-center">
              <Send className="w-4 h-4" />
            </button>
            </div>
          </div>
        </div>
      )}

      {/* Fullscreen image lightbox (progress photos, annotated measurements, etc.) */}
      {lightbox && (
        <div data-testid="jarvis-lightbox" onClick={() => setLightbox(null)}
          className="fixed inset-0 z-[80] bg-black/90 flex items-center justify-center p-4"
          style={{ backdropFilter: "blur(4px)" }}>
          {lightbox.title && (
            <div className="absolute top-4 left-1/2 -translate-x-1/2 max-w-[80vw] text-center text-cyan-200 text-sm font-semibold bg-white/10 rounded-full px-4 py-1.5 truncate"
              data-testid="jarvis-lightbox-title">
              {lightbox.title}
            </div>
          )}
          <button type="button" onClick={() => setLightbox(null)} data-testid="jarvis-lightbox-close"
            className="absolute top-4 right-4 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center">
            <X className="w-6 h-6" />
          </button>
          {lightbox.imgs.length > 1 && (
            <>
              <button type="button" data-testid="jarvis-lightbox-prev"
                onClick={(e) => { e.stopPropagation(); setLightbox((lb) => ({ ...lb, idx: (lb.idx - 1 + lb.imgs.length) % lb.imgs.length })); }}
                className="absolute left-3 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 text-white text-2xl flex items-center justify-center">‹</button>
              <button type="button" data-testid="jarvis-lightbox-next"
                onClick={(e) => { e.stopPropagation(); setLightbox((lb) => ({ ...lb, idx: (lb.idx + 1) % lb.imgs.length })); }}
                className="absolute right-3 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 text-white text-2xl flex items-center justify-center">›</button>
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/80 text-sm bg-white/10 rounded-full px-3 py-1">
                {lightbox.idx + 1} / {lightbox.imgs.length}
              </div>
            </>
          )}
          <div className="flex flex-col items-center gap-3" onClick={(e) => e.stopPropagation()}
            onTouchStart={(e) => { lbTouchX.current = e.touches[0].clientX; }}
            onTouchEnd={(e) => {
              if (lbTouchX.current == null) return;
              const dx = e.changedTouches[0].clientX - lbTouchX.current;
              lbTouchX.current = null;
              if (lightbox.imgs.length < 2) return;
              if (dx < -45) setLightbox((lb) => ({ ...lb, idx: (lb.idx + 1) % lb.imgs.length }));
              else if (dx > 45) setLightbox((lb) => ({ ...lb, idx: (lb.idx - 1 + lb.imgs.length) % lb.imgs.length }));
            }}>
            <img src={lightbox.imgs[lightbox.idx]} alt="foto progres" data-testid="jarvis-lightbox-img"
              className="max-w-[95vw] max-h-[84vh] object-contain rounded-lg shadow-2xl" />
            {lightbox.captions?.[lightbox.idx] && (
              <div className="max-w-[90vw] text-center text-white/85 text-sm bg-white/10 rounded-full px-4 py-1.5 truncate"
                data-testid="jarvis-lightbox-caption">
                {lightbox.captions[lightbox.idx]}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Futuristic SF brief panel — news / weather / today's tasks */}
      {brief && (
        <div data-testid="jarvis-brief" onClick={() => setBrief(null)}
          className="fixed inset-0 z-[85] flex items-center justify-center p-4"
          style={{ background: "radial-gradient(circle at 50% 20%, rgba(6,182,212,0.12), rgba(2,6,23,0.92) 60%)", backdropFilter: "blur(6px)" }}>
          <div onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-2xl max-h-[86vh] overflow-y-auto dsc-scroll rounded-2xl border border-cyan-400/40 bg-slate-950/85 shadow-[0_0_60px_rgba(6,182,212,0.35)]"
            style={{ animation: "jbriefIn .28s cubic-bezier(.2,.9,.3,1)" }}>
            <style>{"@keyframes jbriefIn{from{opacity:0;transform:translateY(18px) scale(.97)}to{opacity:1;transform:none}}@keyframes jscan{0%{transform:translateY(-100%)}100%{transform:translateY(2200%)}}"}</style>
            {/* scan line */}
            <div className="pointer-events-none absolute inset-x-0 top-0 h-8 bg-gradient-to-b from-cyan-400/25 to-transparent" style={{ animation: "jscan 4s linear infinite" }} />
            <div className="flex items-center gap-2.5 px-5 py-3.5 border-b border-cyan-400/20 sticky top-0 bg-slate-950/90 backdrop-blur z-10">
              <span className="w-8 h-8 rounded-lg bg-cyan-500/15 text-cyan-300 flex items-center justify-center"><Sparkles className="w-4 h-4" /></span>
              <span className="text-cyan-100 font-black tracking-wide text-sm uppercase">{brief.title || "Jarvis Brief"}</span>
              <button type="button" onClick={() => setBrief(null)} data-testid="jarvis-brief-close"
                className="ml-auto w-9 h-9 rounded-full bg-white/5 hover:bg-white/15 text-cyan-200 flex items-center justify-center"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-4 space-y-3">
              {brief.weather && (
                <div className="rounded-xl border border-cyan-400/20 bg-cyan-500/5 p-4" data-testid="jarvis-brief-weather">
                  <div className="flex items-center gap-2 text-cyan-300 text-[11px] font-black uppercase tracking-widest mb-2"><CloudSun className="w-4 h-4" /> Weather</div>
                  <div className="flex items-center gap-5 text-cyan-50">
                    <div className="flex items-center gap-2"><Thermometer className="w-5 h-5 text-cyan-300" /><span className="text-2xl font-black">{brief.weather.temp ?? brief.weather.temperature ?? "—"}°</span></div>
                    {(brief.weather.wind ?? brief.weather.wind_speed) != null && <div className="flex items-center gap-1.5 text-sm text-cyan-200/80"><Wind className="w-4 h-4" /> {brief.weather.wind ?? brief.weather.wind_speed} m/s</div>}
                    {(brief.weather.precip ?? brief.weather.precipitation) != null && <div className="flex items-center gap-1.5 text-sm text-cyan-200/80"><Droplets className="w-4 h-4" /> {brief.weather.precip ?? brief.weather.precipitation} mm</div>}
                    {(brief.weather.city || brief.weather.summary) && <div className="ml-auto text-sm text-cyan-200/70">{brief.weather.city || brief.weather.summary}</div>}
                  </div>
                </div>
              )}
              {Array.isArray(brief.news) && brief.news.length > 0 && (
                <div className="rounded-xl border border-cyan-400/20 bg-white/[0.02] p-4" data-testid="jarvis-brief-news">
                  <div className="flex items-center gap-2 text-cyan-300 text-[11px] font-black uppercase tracking-widest mb-2"><Newspaper className="w-4 h-4" /> Headlines</div>
                  <div className="space-y-1.5">
                    {brief.news.map((a, i) => (
                      <a key={i} href={a.link || "#"} target="_blank" rel="noreferrer" data-testid="jarvis-brief-news-item"
                        className="group flex items-start gap-2 rounded-lg px-2 py-1.5 hover:bg-cyan-500/10 transition-colors">
                        <span className="text-cyan-400/70 text-xs font-mono mt-0.5">{String(i + 1).padStart(2, "0")}</span>
                        <span className="flex-1 text-sm text-cyan-50/90 group-hover:text-white leading-snug">{a.title}</span>
                        {a.link && <ExternalLink className="w-3.5 h-3.5 text-cyan-400/60 group-hover:text-cyan-300 mt-0.5 flex-shrink-0" />}
                      </a>
                    ))}
                  </div>
                </div>
              )}
              {brief.tasks && (
                <div className="rounded-xl border border-cyan-400/20 bg-white/[0.02] p-4" data-testid="jarvis-brief-tasks">
                  <div className="flex items-center gap-2 text-cyan-300 text-[11px] font-black uppercase tracking-widest mb-2"><ListChecks className="w-4 h-4" /> Today</div>
                  {(brief.tasks.today_plans || []).length === 0 && (brief.tasks.todos || []).length === 0 && (brief.tasks.recent_plans || []).length === 0 && (
                    <div className="text-sm text-cyan-200/50">Nothing scheduled yet.</div>
                  )}
                  {(brief.tasks.today_plans || []).map((p, i) => (
                    <div key={`p${i}`} className="flex items-start gap-2 py-1">
                      <CalendarDays className="w-4 h-4 text-cyan-300 mt-0.5 flex-shrink-0" />
                      <div><div className="text-sm font-bold text-cyan-50">{p.title}</div>{p.body && <div className="text-xs text-cyan-200/70">{p.body}</div>}</div>
                    </div>
                  ))}
                  {(brief.tasks.todos || []).map((it, i) => (
                    <div key={`t${i}`} className="flex items-center gap-2 py-1 text-sm text-cyan-50/90">
                      <span className={`w-3.5 h-3.5 rounded-[4px] border flex items-center justify-center ${it.done ? "bg-cyan-400 border-cyan-400" : "border-cyan-400/50"}`}>{it.done && <Check className="w-2.5 h-2.5 text-slate-900" />}</span>
                      <span className={it.done ? "line-through text-cyan-200/40" : ""}>{it.text}</span>
                    </div>
                  ))}
                  {(brief.tasks.today_plans || []).length === 0 && (brief.tasks.recent_plans || []).slice(0, 3).map((p, i) => (
                    <div key={`r${i}`} className="flex items-start gap-2 py-1 opacity-70">
                      <CalendarDays className="w-4 h-4 text-cyan-300/70 mt-0.5 flex-shrink-0" />
                      <div><div className="text-sm font-semibold text-cyan-50/80">{p.title}<span className="ml-2 text-[10px] text-cyan-300/60">{p.date}</span></div></div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
